import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Archive,
  Check,
  CheckCircle2,
  ChevronDown,
  CircleSlash,
  Clock,
  FileSearch,
  FileText,
  FileVideo,
  Layers,
  Library,
  Loader2,
  MonitorPlay,
  Pencil,
  Play,
  Plus,
  Rows3,
  Search,
  Scissors,
  Tags,
  Trash2,
  Upload,
  Video,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { toast } from 'sonner';

import DomRecordingPlayer from '@/components/content-library/DomRecordingPlayer';
import type { PresentationUploadResult } from '@/components/content-library/PresentationUploadDialog';
import { PresentationUploadDialog } from '@/components/content-library/PresentationUploadDialog';
import { VideoUploadDialog } from '@/components/content-library/VideoUploadDialog';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useOrganization } from '@/hooks/useOrganization';
import contentLibraryService, {
  type OrganizationSegmentCatalogItem,
  type PresentationDeck,
  type SegmentCatalogAction,
  type SegmentCatalogStatus,
  type Slide,
  type SlideCatalogStatus,
  type Video as SourceVideo,
} from '@/lib/api/contentLibrary';

type AssetMode = 'segments' | 'slides';
type AssetsTab = 'all' | 'active' | 'staged' | 'library' | 'discard';
type SegmentDecision = SegmentCatalogAction;

type SlideCatalogItem = {
  slide: Slide;
  deck: PresentationDeck;
  slideId: string;
};

type SourceVideoStats = {
  segmentCount: number;
  missingClipCount: number;
};

type SlideDraft = {
  title: string;
  contentSummary: string;
  keyPoints: string;
  visualDescription: string;
  topics: string;
  useCase: string;
};

const tabLabels: Record<AssetsTab, string> = {
  all: 'All',
  active: 'Active',
  staged: 'Staged',
  library: 'Library',
  discard: 'Discarded',
};

const statusToTab: Record<SegmentCatalogStatus | SlideCatalogStatus, Exclude<AssetsTab, 'all'>> = {
  active: 'active',
  pending: 'staged',
  library_only: 'library',
  ignored: 'discard',
};

const catalogStatusCopy: Record<SegmentCatalogStatus | SlideCatalogStatus, string> = {
  active: 'Active',
  pending: 'Staged',
  library_only: 'Library',
  ignored: 'Discarded',
};

const slideCatalogStatuses = new Set<SlideCatalogStatus>([
  'active',
  'pending',
  'library_only',
  'ignored',
]);

const decisionCopy: Record<SegmentDecision, string> = {
  activate: 'Add to Agent',
  replace_matched: 'Replace match',
  library_only: 'Move to Library',
  ignore: 'Discard',
  update_group: 'Update Group',
};

const recommendationCopy: Record<string, string> = {
  new_candidate: 'Suggested add',
  possible_duplicate: 'Possible duplicate',
  possible_replacement: 'Possible replacement',
  library_only_candidate: 'Suggested library',
};

const formatTime = (value?: number | null) => {
  if (value == null || Number.isNaN(value)) return '0:00';
  const totalSeconds = Math.max(0, Math.floor(value));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const truncateText = (value: string | null | undefined, length = 170) => {
  if (!value) return '';
  return value.length > length ? `${value.slice(0, length).trim()}...` : value;
};

const normalizeGroupValue = (value?: string | null) => {
  const text = (value || '').trim();
  return text ? text.toLowerCase().replace(/[\s_]+/g, '-') : '';
};

const getSegmentTitle = (segment: OrganizationSegmentCatalogItem) =>
  segment.label || segment.summary || `Segment ${segment.segment_ref || segment.segment_id.slice(0, 8)}`;

const getSegmentSource = (segment: OrganizationSegmentCatalogItem) =>
  segment.video_title || segment.video_filename || 'Untitled video';

const getSegmentSearchText = (segment: OrganizationSegmentCatalogItem) =>
  [
    segment.label,
    segment.summary,
    segment.description,
    segment.ai_context,
    segment.reason,
    segment.content_type,
    segment.playlist_group,
    segment.recommended_action,
    getSegmentSource(segment),
    ...(segment.tags || []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

const getDeckTitle = (deck: PresentationDeck) => deck.title || deck.filename || 'Untitled deck';

const getSlideId = (slide: Slide) =>
  slide.id || (slide as any).slide_id || `${slide.deck_id}-${slide.slide_number}`;

const getSlideTitle = (slide: Slide) =>
  slide.title || slide.content_summary || `Slide ${slide.slide_number}`;

const getSlideCatalogStatus = (item: SlideCatalogItem): SlideCatalogStatus => {
  if (item.slide.catalog_status && slideCatalogStatuses.has(item.slide.catalog_status)) {
    return item.slide.catalog_status;
  }
  return item.slide.is_active === false ? 'library_only' : 'active';
};

const isSlideAgentActive = (item: SlideCatalogItem) =>
  getSlideCatalogStatus(item) === 'active';

const getSlideSearchText = (item: SlideCatalogItem) =>
  [
    getDeckTitle(item.deck),
    item.slide.title,
    item.slide.content_summary,
    item.slide.visual_description,
    item.slide.use_case,
    item.slide.playlist_group,
    ...(item.slide.key_points || []),
    ...(item.slide.topics || []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

const createSlideDraft = (slide?: Slide | null): SlideDraft => ({
  title: slide?.title || '',
  contentSummary: slide?.content_summary || '',
  keyPoints: (slide?.key_points || []).join('\n'),
  visualDescription: slide?.visual_description || '',
  topics: (slide?.topics || []).join(', '),
  useCase: slide?.use_case || '',
});

const normalizeMultilineList = (value: string) =>
  value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeCommaList = (value: string) =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const areSlideDraftsEqual = (a: SlideDraft, b: SlideDraft) =>
  a.title === b.title &&
  a.contentSummary === b.contentSummary &&
  a.keyPoints === b.keyPoints &&
  a.visualDescription === b.visualDescription &&
  a.topics === b.topics &&
  a.useCase === b.useCase;

const getDefaultDecision = (segment: OrganizationSegmentCatalogItem): SegmentDecision => {
  switch (segment.recommended_action) {
    case 'possible_duplicate':
    case 'library_only_candidate':
      return 'library_only';
    case 'possible_replacement':
      return segment.matched_segment ? 'replace_matched' : 'activate';
    case 'new_candidate':
    default:
      return 'activate';
  }
};

const parseAssetsTab = (tab: string | null): AssetsTab =>
  tab === 'all' || tab === 'active' || tab === 'staged' || tab === 'library' || tab === 'discard'
    ? tab
    : 'active';

const parseAssetMode = (mode: string | null): AssetMode =>
  mode === 'slides' ? 'slides' : 'segments';

const parseSourceParam = (source: string | null): string[] =>
  source ? source.split(',').filter(Boolean) : [];

const renderListRowSkeleton = (key: number) => (
  <div
    key={key}
    className="flex gap-3 rounded-[8px] border border-[hsl(var(--input))] bg-secondary/40 p-3"
  >
    <Skeleton className="h-[74px] w-[120px] shrink-0 rounded-md" />
    <div className="flex min-w-0 flex-1 flex-col gap-2 py-0.5">
      <div className="flex flex-wrap items-center gap-2">
        <Skeleton className="h-4 w-[180px]" />
        <Skeleton className="h-5 w-14 rounded-full" />
      </div>
      <Skeleton className="h-3 w-[60%]" />
      <Skeleton className="h-3 w-[80%]" />
      <div className="mt-1 flex flex-wrap gap-1.5">
        <Skeleton className="h-5 w-16 rounded-full" />
        <Skeleton className="h-5 w-12 rounded-full" />
      </div>
    </div>
    <Skeleton className="h-7 w-20 shrink-0 self-start rounded-md" />
  </div>
);

const ContentLibraryPage: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const orgId = currentOrganization?.id;

  const [assetsTab, setAssetsTab] = useState<AssetsTab>(() => {
    return parseAssetsTab(searchParams.get('tab'));
  });
  const [assetMode, setAssetMode] = useState<AssetMode>(() => {
    return parseAssetMode(searchParams.get('asset'));
  });
  const [segmentSearch, setSegmentSearch] = useState('');
  const [slideSearch, setSlideSearch] = useState('');
  const [selectedSourceVideoIds, setSelectedSourceVideoIds] = useState<string[]>(() => {
    return parseSourceParam(searchParams.get('source'));
  });
  const [isSourceFilterOpen, setIsSourceFilterOpen] = useState(false);
  const [selectedSegmentGroups, setSelectedSegmentGroups] = useState<string[]>([]);
  const [selectedSlideGroups, setSelectedSlideGroups] = useState<string[]>([]);
  const [isSegmentGroupFilterOpen, setIsSegmentGroupFilterOpen] = useState(false);
  const [isSlideGroupFilterOpen, setIsSlideGroupFilterOpen] = useState(false);
  const [segmentBulkGroupDraft, setSegmentBulkGroupDraft] = useState('');
  const [slideBulkGroupDraft, setSlideBulkGroupDraft] = useState('');
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const [selectedSlideId, setSelectedSlideId] = useState<string | null>(null);
  const [selectedSlideIds, setSelectedSlideIds] = useState<string[]>([]);
  const [slidePendingDeletion, setSlidePendingDeletion] = useState<SlideCatalogItem | null>(null);
  const [slideBulkPendingDeletionIds, setSlideBulkPendingDeletionIds] = useState<string[]>([]);
  const [isEditingSlideInfo, setIsEditingSlideInfo] = useState(false);
  const [slideDraft, setSlideDraft] = useState<SlideDraft>(() => createSlideDraft(null));
  const [pendingSlideGroupOverrides, setPendingSlideGroupOverrides] = useState<Record<string, string>>({});
  const [selectedSegmentIds, setSelectedSegmentIds] = useState<string[]>([]);
  const [pendingSegmentActions, setPendingSegmentActions] = useState<Record<string, SegmentDecision>>({});
  const [pendingGroupOverrides, setPendingGroupOverrides] = useState<Record<string, string>>({});
  const [contextDraft, setContextDraft] = useState('');
  const playbackVideoRef = useRef<HTMLVideoElement | null>(null);
  const segmentsScrollRef = useRef<HTMLDivElement | null>(null);
  const slidesScrollRef = useRef<HTMLDivElement | null>(null);

  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isPresentationUploadDialogOpen, setIsPresentationUploadDialogOpen] = useState(false);

  const syncAssetUrl = useCallback((tab: AssetsTab, sources: string[], mode: AssetMode = assetMode) => {
    const next = new URLSearchParams(searchParams);
    if (mode === 'slides') {
      next.set('asset', 'slides');
      next.set('tab', tab);
      if (sources.length > 0) {
        next.set('source', sources.join(','));
      } else {
        next.delete('source');
      }
    } else {
      next.delete('asset');
      next.set('tab', tab);
      if (sources.length > 0) {
        next.set('source', sources.join(','));
      } else {
        next.delete('source');
      }
    }
    next.delete('view');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [assetMode, searchParams, setSearchParams]);

  const setAssetModeAndUrl = useCallback((mode: AssetMode) => {
    setAssetMode(mode);
    setSelectedSegmentIds([]);
    setSelectedSlideIds([]);
    setSelectedSourceVideoIds([]);
    setIsEditingSlideInfo(false);
    const next = new URLSearchParams(searchParams);
    if (mode === 'slides') {
      next.set('asset', 'slides');
      next.set('tab', assetsTab);
      next.delete('source');
    } else {
      next.delete('asset');
      next.set('tab', assetsTab);
      next.delete('source');
    }
    next.delete('view');
    setSearchParams(next, { replace: true });
  }, [assetsTab, searchParams, setSearchParams]);

  useEffect(() => {
    if (location.pathname !== '/content-library') return;
    const params = new URLSearchParams(location.search);
    const nextMode = parseAssetMode(params.get('asset'));
    const nextTab = parseAssetsTab(params.get('tab'));
    const nextSources = parseSourceParam(params.get('source'));
    setAssetMode((current) => (current === nextMode ? current : nextMode));
    setAssetsTab((current) => (current === nextTab ? current : nextTab));
    setSelectedSourceVideoIds((current) => {
      if (current.join(',') === nextSources.join(',')) return current;
      setSelectedSegmentIds([]);
      setSelectedSlideIds([]);
      return nextSources;
    });
    if (params.has('view')) {
      params.delete('view');
      setSearchParams(params, { replace: true });
    }
  }, [location.pathname, location.search, setSearchParams]);

  const uploadMutation = useMutation({
    mutationFn: async (data: {
      file: File;
      title?: string;
      customInstructions?: string;
      metadataInstructions?: string;
      segmentationMode: 'none' | 'auto';
      playlistPolicy: 'mixable' | 'standalone';
      playlistGroup?: string;
    }) => {
      if (!orgId) throw new Error('Organization ID required');

      const requestResponse = await contentLibraryService.requestUpload({
        organization_id: orgId,
        filename: data.file.name,
        title: data.title,
        playlist_policy: data.playlistPolicy,
        playlist_group: data.playlistGroup,
      });
      if (!requestResponse.success || !requestResponse.data) {
        throw new Error(requestResponse.error || 'Failed to get upload URL');
      }

      const { video_id, upload_url } = requestResponse.data;
      await contentLibraryService.uploadToSignedUrl(upload_url, data.file);

      const confirmResponse = await contentLibraryService.confirmUpload(video_id, {
        segmentation_mode: data.segmentationMode,
        custom_instructions: data.segmentationMode === 'auto' ? data.customInstructions : undefined,
        metadata_instructions: data.segmentationMode === 'auto' ? data.metadataInstructions : undefined,
        playlist_policy: data.playlistPolicy,
        playlist_group: data.playlistGroup ?? null,
      });
      if (!confirmResponse.success || !confirmResponse.data) {
        throw new Error(confirmResponse.error || 'Failed to confirm upload');
      }

      return { video_id, status: confirmResponse.data.status, segmentationMode: data.segmentationMode };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['organization-videos', orgId] });
      queryClient.invalidateQueries({ queryKey: ['organization-segments', orgId] });
      setIsUploadDialogOpen(false);
      if (result.segmentationMode === 'none') {
        toast.success('Capture uploaded. Ready to clip manually.');
        navigate(`/content-library/video/${result.video_id}`);
        return;
      }
      toast.success('Capture uploaded. Segmentation is in progress.');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const generateClipsMutation = useMutation({
    mutationFn: async (videoId: string) => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await contentLibraryService.generateVideoClips(videoId, {
        organization_id: orgId,
        overwrite: false,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to start clip generation');
      }
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['organization-segments', orgId] });
      if (!data.started) {
        toast.success(data.message);
        return;
      }
      toast.success(`Queued ${data.clips_queued} clip asset${data.clips_queued === 1 ? '' : 's'} for generation`);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const handleUploadOnePresentation = useCallback(
    async (
      file: File,
      opts: { title?: string; description?: string },
    ): Promise<PresentationUploadResult> => {
      if (!orgId) return { ok: false, error: 'No organization selected' };
      try {
        const response = await contentLibraryService.uploadPresentation({
          organization_id: orgId,
          file,
          title: opts.title,
          description: opts.description,
        });
        if (!response.success || !response.data) {
          return { ok: false, error: response.error || 'Failed to upload presentation' };
        }
        return {
          ok: true,
          truncationMessage: response.data.deck.truncation_warning?.exceeds_cap
            ? response.data.deck.truncation_warning.message
            : null,
        };
      } catch (err) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : 'Failed to upload presentation',
        };
      }
    },
    [orgId],
  );

  const handlePresentationUploadComplete = useCallback(
    (summary: { succeeded: number; failed: number; truncationMessages: string[] }) => {
      if (summary.succeeded > 0) {
        queryClient.invalidateQueries({ queryKey: ['organization-decks', orgId] });
        queryClient.invalidateQueries({ queryKey: ['organization-deck-slides', orgId] });
      }
      if (summary.succeeded > 0 && summary.failed === 0) {
        toast.success(
          summary.succeeded === 1
            ? 'Deck uploaded. Slides will appear in Library after processing.'
            : `${summary.succeeded} decks uploaded. Slides will appear in Library after processing.`,
        );
      } else if (summary.succeeded > 0 && summary.failed > 0) {
        toast.error(
          `${summary.succeeded} of ${summary.succeeded + summary.failed} presentations uploaded. ${summary.failed} failed.`,
        );
      } else if (summary.failed > 0) {
        toast.error(
          summary.failed === 1
            ? 'Presentation upload failed.'
            : `${summary.failed} presentation uploads failed.`,
        );
      }
      if (summary.truncationMessages.length > 0) {
        const preview = summary.truncationMessages.slice(0, 2).join(' | ');
        const remainder = summary.truncationMessages.length - 2;
        toast.warning(remainder > 0 ? `${preview} | +${remainder} more` : preview);
      }
    },
    [orgId, queryClient],
  );

  const activateDeckMutation = useMutation({
    mutationFn: async (deckId: string) => {
      const response = await contentLibraryService.activateDeck(deckId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to activate deck');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-decks', orgId] });
      queryClient.invalidateQueries({ queryKey: ['organization-deck-slides', orgId] });
      toast.success('Deck added to Agent');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deactivateDeckMutation = useMutation({
    mutationFn: async (deckId: string) => {
      const response = await contentLibraryService.deactivateDeck(deckId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to deactivate deck');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-decks', orgId] });
      queryClient.invalidateQueries({ queryKey: ['organization-deck-slides', orgId] });
      toast.success('Deck removed from Agent');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const activateSlideMutation = useMutation({
    mutationFn: async (slideId: string) => {
      const response = await contentLibraryService.activateSlide(slideId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to add slide to Agent');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-decks', orgId] });
      queryClient.invalidateQueries({ queryKey: ['organization-deck-slides', orgId] });
      toast.success('Slide added to Agent');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deactivateSlideMutation = useMutation({
    mutationFn: async (slideId: string) => {
      const response = await contentLibraryService.deactivateSlide(slideId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to remove slide from Agent');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-decks', orgId] });
      queryClient.invalidateQueries({ queryKey: ['organization-deck-slides', orgId] });
      toast.success('Slide removed from Agent');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const bulkSlideActionMutation = useMutation({
    mutationFn: async ({
      slideIds,
      catalogStatus,
    }: {
      slideIds: string[];
      catalogStatus: SlideCatalogStatus;
    }) => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await contentLibraryService.batchUpdateSlideCatalog(orgId, {
        organization_id: orgId,
        updates: slideIds.map((slideId) => ({
          slide_id: slideId,
          catalog_status: catalogStatus,
        })),
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update selected slides');
      }
      return { catalogStatus, count: response.data.updated };
    },
    onSuccess: ({ catalogStatus, count }) => {
      queryClient.invalidateQueries({ queryKey: ['organization-decks', orgId] });
      queryClient.invalidateQueries({ queryKey: ['organization-deck-slides', orgId] });
      setSelectedSlideIds([]);
      const statusCopy = catalogStatus === 'active'
        ? 'added to Agent'
        : catalogStatus === 'library_only'
          ? 'moved to Library'
          : catalogStatus === 'ignored'
            ? 'discarded'
            : 'staged';
      toast.success(`${count} slide${count === 1 ? '' : 's'} ${statusCopy}`);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const slideGroupMutation = useMutation({
    mutationFn: async (
      updates: Array<{
        slide_id: string;
        target_playlist_group: string | null;
      }>,
    ) => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await contentLibraryService.batchUpdateSlideCatalog(orgId, {
        organization_id: orgId,
        updates,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update slide groups');
      }
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['organization-decks', orgId] });
      queryClient.invalidateQueries({ queryKey: ['organization-deck-slides', orgId] });
      setSelectedSlideIds([]);
      setPendingSlideGroupOverrides({});
      toast.success(`${data.updated} slide group update${data.updated === 1 ? '' : 's'} saved`);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateSlideInfoMutation = useMutation({
    mutationFn: async ({
      slideId,
      draft,
    }: {
      slideId: string;
      draft: SlideDraft;
    }) => {
      const response = await contentLibraryService.updateSlide(slideId, {
        title: draft.title.trim(),
        content_summary: draft.contentSummary.trim(),
        key_points: normalizeMultilineList(draft.keyPoints),
        visual_description: draft.visualDescription.trim(),
        topics: normalizeCommaList(draft.topics),
        use_case: draft.useCase.trim(),
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update slide information');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-decks', orgId] });
      queryClient.invalidateQueries({ queryKey: ['organization-deck-slides', orgId] });
      setIsEditingSlideInfo(false);
      toast.success('Slide information updated');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteSlideMutation = useMutation({
    mutationFn: async (slideId: string) => {
      const response = await contentLibraryService.deleteSlide(slideId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to delete slide');
      }
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['organization-decks', orgId] });
      queryClient.invalidateQueries({ queryKey: ['organization-deck-slides', orgId] });
      if (selectedSlideId === data.slide_id) {
        setSelectedSlideId(null);
      }
      setSelectedSlideIds((current) => current.filter((slideId) => slideId !== data.slide_id));
      setSlidePendingDeletion(null);
      toast.success('Slide deleted');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const bulkDeleteSlidesMutation = useMutation({
    mutationFn: async (slideIds: string[]) => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await contentLibraryService.bulkDeleteSlides(orgId, slideIds);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to delete selected slides');
      }
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['organization-decks', orgId] });
      queryClient.invalidateQueries({ queryKey: ['organization-deck-slides', orgId] });
      setSelectedSlideIds([]);
      setSlideBulkPendingDeletionIds([]);
      setSelectedSlideId((current) => (
        current && data.results.some((result) => result.success && result.slide_id === current)
          ? null
          : current
      ));
      toast.success(`${data.deleted} slide${data.deleted === 1 ? '' : 's'} deleted`);
      if (data.failed > 0) {
        toast.error(`${data.failed} selected slide${data.failed === 1 ? '' : 's'} could not be deleted`);
      }
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const segmentCatalogMutation = useMutation({
    mutationFn: async (
      updates: Array<{
        segment_id: string;
        action: SegmentDecision;
        target_playlist_group?: string | null;
      }>,
    ) => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await contentLibraryService.batchUpdateSegmentCatalog(orgId, {
        organization_id: orgId,
        updates,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update segments');
      }
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['organization-segments', orgId] });
      setSelectedSegmentIds([]);
      setPendingSegmentActions({});
      setPendingGroupOverrides({});
      toast.success(`${data.updated} segment decision${data.updated === 1 ? '' : 's'} saved`);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateSegmentContextMutation = useMutation({
    mutationFn: async ({
      segment,
      context,
    }: {
      segment: OrganizationSegmentCatalogItem;
      context: string;
    }) => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await contentLibraryService.updateSegmentMetadata(
        segment.video_id,
        segment.segment_id,
        {
          organization_id: orgId,
          ai_context: context,
        },
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update context');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-segments', orgId] });
      toast.success('Context updated');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const { data: videosData, isLoading: videosLoading, error: videosError } = useQuery({
    queryKey: ['organization-videos', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await contentLibraryService.getOrganizationVideos(orgId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch videos');
      }
      return response.data;
    },
    enabled: !!orgId,
    retry: false,
    refetchInterval: (query) => {
      const vids = query.state.data?.videos;
      const hasProcessing = vids?.some(
        (v) =>
          v.status === 'pending' ||
          v.status === 'processing' ||
          v.status === 'segmenting' ||
          v.status === 'adding_to_collection' ||
          v.status === 'pending_segmentation',
      );
      return hasProcessing ? 5000 : false;
    },
  });

  const { data: decksData, isLoading: decksLoading, error: decksError } = useQuery({
    queryKey: ['organization-decks', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await contentLibraryService.listOrganizationDecks(orgId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch decks');
      }
      return response.data;
    },
    enabled: !!orgId,
    retry: false,
  });

  const {
    data: segmentsData,
    isLoading: segmentsLoading,
    error: segmentsError,
  } = useQuery({
    queryKey: ['organization-segments', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await contentLibraryService.listOrganizationSegments(orgId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch segments');
      }
      return response.data;
    },
    enabled: !!orgId,
    retry: false,
    refetchInterval: assetsTab === 'staged' ? 5000 : false,
  });

  const videos = useMemo(() => videosData?.videos || [], [videosData?.videos]);
  const decks = useMemo<PresentationDeck[]>(() => decksData?.decks || [], [decksData?.decks]);
  const segments = useMemo(() => segmentsData?.segments || [], [segmentsData?.segments]);
  const activeDecks = useMemo(() => decks.filter((deck) => deck.is_active), [decks]);
  const availableGroups = useMemo(
    () => segmentsData?.available_groups || [],
    [segmentsData?.available_groups],
  );
  const query = segmentSearch.trim().toLowerCase();
  const isSearchMode = query.length > 0;
  const slideQuery = slideSearch.trim().toLowerCase();
  const isSlideSearchMode = slideQuery.length > 0;
  const deckIdsKey = useMemo(
    () => decks.map((deck) => deck.deck_id).sort().join(','),
    [decks],
  );

  const {
    data: deckSlideData,
    isLoading: deckSlidesLoading,
    error: deckSlidesError,
  } = useQuery({
    queryKey: ['organization-deck-slides', orgId, deckIdsKey],
    queryFn: async () => {
      const responses = await Promise.all(
        decks.map(async (deck) => {
          const response = await contentLibraryService.getDeckById(deck.deck_id);
          if (!response.success || !response.data) {
            throw new Error(response.error || `Failed to fetch slides for ${deck.title || deck.filename}`);
          }
          return {
            deck: response.data.deck || deck,
            slides: response.data.slides || [],
          };
        }),
      );
      return responses;
    },
    enabled: !!orgId && decks.length > 0 && assetMode === 'slides',
    retry: false,
  });

  const segmentTabCounts = useMemo(() => ({
    all: segments.length,
    active: segments.filter((segment) => segment.catalog_status === 'active').length,
    staged: segments.filter((segment) => segment.catalog_status === 'pending').length,
    library: segments.filter((segment) => segment.catalog_status === 'library_only').length,
    discard: segments.filter((segment) => segment.catalog_status === 'ignored').length,
  }), [segments]);

  const sourceVideoStats = useMemo(() => {
    const stats = new Map<string, SourceVideoStats>();
    segments.forEach((segment) => {
      const existing = stats.get(segment.video_id) || { segmentCount: 0, missingClipCount: 0 };
      existing.segmentCount += 1;
      if (segment.playback_type === 'video' && !segment.has_clip_asset) {
        existing.missingClipCount += 1;
      }
      stats.set(segment.video_id, existing);
    });
    return stats;
  }, [segments]);

  const sourceVideosNeedingSetup = useMemo(() => videos.filter((video) => {
    const stats = sourceVideoStats.get(video.video_id) || { segmentCount: 0, missingClipCount: 0 };
    const isReadyForManualClipping = video.status === 'ready' || video.status === 'pending_segmentation';
    return (
      (isReadyForManualClipping && stats.segmentCount === 0) ||
      (stats.segmentCount > 1 && stats.missingClipCount > 0)
    );
  }), [sourceVideoStats, videos]);

  const selectedSourceSet = useMemo(() => new Set(selectedSourceVideoIds), [selectedSourceVideoIds]);
  const segmentSourceOptions = useMemo(() => {
    const sources = new Map<string, { videoId: string; label: string; count: number; staged: number }>();
    segments.forEach((segment) => {
      const existing = sources.get(segment.video_id);
      if (existing) {
        existing.count += 1;
        if (segment.catalog_status === 'pending') existing.staged += 1;
      } else {
        sources.set(segment.video_id, {
          videoId: segment.video_id,
          label: getSegmentSource(segment),
          count: 1,
          staged: segment.catalog_status === 'pending' ? 1 : 0,
        });
      }
    });
    return Array.from(sources.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [segments]);

  const getEffectiveSegmentGroup = useCallback((segment: OrganizationSegmentCatalogItem) => (
    pendingGroupOverrides[segment.segment_id] !== undefined
      ? normalizeGroupValue(pendingGroupOverrides[segment.segment_id])
      : normalizeGroupValue(segment.playlist_group)
  ), [pendingGroupOverrides]);

  const getEffectiveSlideGroup = useCallback((item: SlideCatalogItem) => (
    pendingSlideGroupOverrides[item.slideId] !== undefined
      ? normalizeGroupValue(pendingSlideGroupOverrides[item.slideId])
      : normalizeGroupValue(item.slide.playlist_group)
  ), [pendingSlideGroupOverrides]);

  const availableSegmentGroups = useMemo(() => {
    const groups = new Set<string>();
    availableGroups.forEach((group) => {
      const normalized = normalizeGroupValue(group);
      if (normalized) groups.add(normalized);
    });
    segments.forEach((segment) => {
      const normalized = getEffectiveSegmentGroup(segment);
      if (normalized) groups.add(normalized);
    });
    return Array.from(groups).sort((a, b) => a.localeCompare(b));
  }, [availableGroups, getEffectiveSegmentGroup, segments]);

  const filteredSegments = useMemo(() => {
    return segments.filter((segment) => {
      const matchesTab = isSearchMode || assetsTab === 'all' || statusToTab[segment.catalog_status] === assetsTab;
      const matchesQuery = query ? getSegmentSearchText(segment).includes(query) : true;
      const matchesSource =
        selectedSourceVideoIds.length === 0 || selectedSourceSet.has(segment.video_id);
      const group = getEffectiveSegmentGroup(segment);
      const matchesGroup = selectedSegmentGroups.length === 0 || selectedSegmentGroups.includes(group);
      return matchesTab && matchesQuery && matchesSource && matchesGroup;
    });
  }, [
    assetsTab,
    getEffectiveSegmentGroup,
    isSearchMode,
    query,
    selectedSegmentGroups,
    selectedSourceSet,
    selectedSourceVideoIds.length,
    segments,
  ]);

  const slideItems = useMemo<SlideCatalogItem[]>(() => {
    const deckById = new Map(decks.map((deck) => [deck.deck_id, deck]));
    return (deckSlideData || [])
      .flatMap(({ deck, slides }) => {
        const hydratedDeck = deckById.get(deck.deck_id) || deck;
        return slides.map((slide) => {
          const catalogStatus = slide.catalog_status && slideCatalogStatuses.has(slide.catalog_status)
            ? slide.catalog_status
            : slide.is_active === false
              ? 'library_only'
              : 'active';
          return {
            slide: {
              ...slide,
              catalog_status: catalogStatus,
              is_active: catalogStatus === 'active',
            },
            deck: hydratedDeck,
            slideId: getSlideId(slide),
          };
        });
      })
      .sort((a, b) => {
        const deckCompare = getDeckTitle(a.deck).localeCompare(getDeckTitle(b.deck));
        return deckCompare || a.slide.slide_number - b.slide.slide_number;
      });
  }, [deckSlideData, decks]);

  const slideTabCounts = useMemo(() => ({
    all: slideItems.length,
    active: slideItems.filter((item) => getSlideCatalogStatus(item) === 'active').length,
    staged: slideItems.filter((item) => getSlideCatalogStatus(item) === 'pending').length,
    library: slideItems.filter((item) => getSlideCatalogStatus(item) === 'library_only').length,
    discard: slideItems.filter((item) => getSlideCatalogStatus(item) === 'ignored').length,
  }), [slideItems]);

  const slideSourceOptions = useMemo(() => {
    const sources = new Map<string, { videoId: string; label: string; count: number; staged: number }>();
    slideItems.forEach((item) => {
      const sourceId = item.deck.deck_id;
      const existing = sources.get(sourceId);
      if (existing) {
        existing.count += 1;
      } else {
        sources.set(sourceId, {
          videoId: sourceId,
          label: getDeckTitle(item.deck),
          count: 1,
          staged: 0,
        });
      }
    });
    return Array.from(sources.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [slideItems]);

  const sourceOptions = useMemo(
    () => (assetMode === 'slides' ? slideSourceOptions : segmentSourceOptions),
    [assetMode, segmentSourceOptions, slideSourceOptions],
  );
  const sourceItemCount = assetMode === 'slides' ? slideItems.length : segments.length;
  const selectedSourceLabel = useMemo(() => {
    if (selectedSourceVideoIds.length === 0) return null;
    const selectedSources = sourceOptions.filter((source) => selectedSourceSet.has(source.videoId));
    if (selectedSources.length === 1) return selectedSources[0].label;
    return `${selectedSourceVideoIds.length} sources`;
  }, [selectedSourceSet, selectedSourceVideoIds.length, sourceOptions]);

  const availableSlideGroups = useMemo(() => {
    const groups = new Set<string>();
    slideItems.forEach((item) => {
      const normalized = getEffectiveSlideGroup(item);
      if (normalized) groups.add(normalized);
    });
    return Array.from(groups).sort((a, b) => a.localeCompare(b));
  }, [getEffectiveSlideGroup, slideItems]);

  const filteredSlideItems = useMemo(() => {
    return slideItems.filter((item) => {
      const status = getSlideCatalogStatus(item);
      const matchesTab = isSlideSearchMode || assetsTab === 'all' || statusToTab[status] === assetsTab;
      const matchesQuery = slideQuery ? getSlideSearchText(item).includes(slideQuery) : true;
      const matchesSource =
        selectedSourceVideoIds.length === 0 || selectedSourceSet.has(item.deck.deck_id);
      const group = getEffectiveSlideGroup(item);
      const matchesGroup = selectedSlideGroups.length === 0 || selectedSlideGroups.includes(group);
      return matchesTab && matchesQuery && matchesSource && matchesGroup;
    });
  }, [
    assetsTab,
    getEffectiveSlideGroup,
    isSlideSearchMode,
    selectedSlideGroups,
    selectedSourceSet,
    selectedSourceVideoIds.length,
    slideItems,
    slideQuery,
  ]);

  const selectedSlideIdSet = useMemo(
    () => new Set(selectedSlideIds),
    [selectedSlideIds],
  );

  const visibleSlideIds = useMemo(
    () => filteredSlideItems.map((item) => item.slideId),
    [filteredSlideItems],
  );

  const selectedVisibleSlideIds = useMemo(
    () => visibleSlideIds.filter((slideId) => selectedSlideIdSet.has(slideId)),
    [selectedSlideIdSet, visibleSlideIds],
  );

  const slideSelectAllState =
    selectedVisibleSlideIds.length === 0
      ? false
      : selectedVisibleSlideIds.length === visibleSlideIds.length
        ? true
        : 'indeterminate';

  const activeSlideCount = useMemo(
    () => slideItems.filter(isSlideAgentActive).length,
    [slideItems],
  );

  const selectedSegment = useMemo(
    () =>
      segments.find((segment) => segment.segment_id === selectedSegmentId) ||
      filteredSegments[0] ||
      null,
    [filteredSegments, segments, selectedSegmentId],
  );

  const selectedSlide = useMemo(
    () =>
      slideItems.find((item) => item.slideId === selectedSlideId) ||
      filteredSlideItems[0] ||
      null,
    [filteredSlideItems, selectedSlideId, slideItems],
  );

  const segmentsVirtualizer = useVirtualizer({
    count: filteredSegments.length,
    getScrollElement: () => segmentsScrollRef.current,
    estimateSize: () => 118,
    overscan: 8,
    getItemKey: (index) => filteredSegments[index]?.segment_id ?? index,
  });

  const slidesVirtualizer = useVirtualizer({
    count: filteredSlideItems.length,
    getScrollElement: () => slidesScrollRef.current,
    estimateSize: () => 124,
    overscan: 8,
    getItemKey: (index) => filteredSlideItems[index]?.slideId ?? index,
  });

  useEffect(() => {
    if (!selectedSegmentId || filteredSegments.length === 0) return;
    const index = filteredSegments.findIndex((segment) => segment.segment_id === selectedSegmentId);
    if (index >= 0) {
      segmentsVirtualizer.scrollToIndex(index, { align: 'auto' });
    }
  }, [filteredSegments, segmentsVirtualizer, selectedSegmentId]);

  useEffect(() => {
    if (!selectedSlideId || filteredSlideItems.length === 0) return;
    const index = filteredSlideItems.findIndex((item) => item.slideId === selectedSlideId);
    if (index >= 0) {
      slidesVirtualizer.scrollToIndex(index, { align: 'auto' });
    }
  }, [filteredSlideItems, selectedSlideId, slidesVirtualizer]);

  useEffect(() => {
    if (!selectedSegment && selectedSegmentId) {
      setSelectedSegmentId(null);
    }
  }, [selectedSegment, selectedSegmentId]);

  useEffect(() => {
    if (!selectedSlide && selectedSlideId) {
      setSelectedSlideId(null);
    }
  }, [selectedSlide, selectedSlideId]);

  useEffect(() => {
    const availableSlideIds = new Set(slideItems.map((item) => item.slideId));
    setSelectedSlideIds((current) => {
      const next = current.filter((slideId) => availableSlideIds.has(slideId));
      return next.length === current.length ? current : next;
    });
    setPendingSlideGroupOverrides((current) => {
      const next = Object.fromEntries(
        Object.entries(current).filter(([slideId]) => availableSlideIds.has(slideId)),
      );
      return Object.keys(next).length === Object.keys(current).length ? current : next;
    });
  }, [slideItems]);

  useEffect(() => {
    if (!isEditingSlideInfo) {
      const nextDraft = createSlideDraft(selectedSlide?.slide ?? null);
      setSlideDraft((current) => (
        areSlideDraftsEqual(current, nextDraft) ? current : nextDraft
      ));
    }
  }, [isEditingSlideInfo, selectedSlide]);

  useEffect(() => {
    setContextDraft(selectedSegment?.ai_context || '');
  }, [selectedSegment?.ai_context, selectedSegment?.segment_id]);

  useEffect(() => {
    const video = playbackVideoRef.current;
    if (!video || !selectedSegment || selectedSegment.playback_type !== 'video') return;
    video.currentTime = selectedSegment.playback_start_time || 0;
  }, [selectedSegment?.segment_id, selectedSegment?.playback_start_time, selectedSegment?.playback_type]);

  const isDeckReady = (deck: PresentationDeck) =>
    typeof deck.total_slides === 'number' && deck.total_slides > 0;

  const getPlannedDecision = useCallback((segment: OrganizationSegmentCatalogItem): SegmentDecision | null => {
    return pendingSegmentActions[segment.segment_id] || (
      segment.catalog_status === 'pending' ? getDefaultDecision(segment) : null
    );
  }, [pendingSegmentActions]);

  const getPendingLabel = (segment: OrganizationSegmentCatalogItem) => {
    const explicit = pendingSegmentActions[segment.segment_id];
    if (!explicit) return null;
    return decisionCopy[explicit];
  };

  const stageSegments = (segmentIds: string[], action: SegmentDecision) => {
    if (segmentIds.length === 0) return;
    setPendingSegmentActions((current) => {
      const next = { ...current };
      segmentIds.forEach((segmentId) => {
        const segment = segments.find((candidate) => candidate.segment_id === segmentId);
        if (!segment) return;
        const savedDecision =
          segment.catalog_status === 'active'
            ? 'activate'
            : segment.catalog_status === 'library_only'
              ? 'library_only'
              : segment.catalog_status === 'ignored'
                ? 'ignore'
                : getDefaultDecision(segment);
        if (savedDecision === action) {
          delete next[segmentId];
        } else {
          next[segmentId] = action;
        }
      });
      return next;
    });
    setSelectedSegmentIds([]);
  };

  const setSegmentDecision = (segment: OrganizationSegmentCatalogItem, action: SegmentDecision) => {
    stageSegments([segment.segment_id], action);
  };

  const visiblePendingUpdates = useMemo(() => {
    if (assetsTab !== 'staged') return [];
    return filteredSegments
      .filter((segment) => segment.catalog_status === 'pending')
      .map((segment) => ({
        segment_id: segment.segment_id,
        action: getPlannedDecision(segment) || getDefaultDecision(segment),
        target_playlist_group:
          pendingGroupOverrides[segment.segment_id] !== undefined
            ? pendingGroupOverrides[segment.segment_id] || null
            : segment.playlist_group ?? null,
      }));
  }, [assetsTab, filteredSegments, getPlannedDecision, pendingGroupOverrides]);

  const explicitPendingUpdates = useMemo(() => {
    const updates = new Map<string, {
      segment_id: string;
      action: SegmentDecision;
      target_playlist_group?: string | null;
    }>();

    Object.entries(pendingSegmentActions).forEach(([segment_id, action]) => {
      updates.set(segment_id, {
        segment_id,
        action,
        target_playlist_group:
          pendingGroupOverrides[segment_id] !== undefined
            ? pendingGroupOverrides[segment_id] || null
            : undefined,
      });
    });

    Object.entries(pendingGroupOverrides).forEach(([segment_id, group]) => {
      const segment = segments.find((candidate) => candidate.segment_id === segment_id);
      const savedGroup = normalizeGroupValue(segment?.playlist_group);
      const nextGroup = normalizeGroupValue(group);
      if (!segment || savedGroup === nextGroup || updates.has(segment_id)) return;
      updates.set(segment_id, {
        segment_id,
        action: 'update_group',
        target_playlist_group: group || null,
      });
    });

    return Array.from(updates.values());
  }, [pendingGroupOverrides, pendingSegmentActions, segments]);

  const pendingSegmentUpdates = assetsTab === 'staged' && visiblePendingUpdates.length > 0
    ? visiblePendingUpdates
    : explicitPendingUpdates;
  const explicitPendingCount = explicitPendingUpdates.length;
  const pendingSaveCount = pendingSegmentUpdates.length;

  const savePendingSegmentChanges = () => {
    if (pendingSegmentUpdates.length === 0) return;
    segmentCatalogMutation.mutate(pendingSegmentUpdates);
  };

  const pendingSlideGroupUpdates = useMemo(() => (
    Object.entries(pendingSlideGroupOverrides).flatMap(([slide_id, group]) => {
      const item = slideItems.find((candidate) => candidate.slideId === slide_id);
      if (!item) return [];
      const savedGroup = normalizeGroupValue(item.slide.playlist_group);
      const nextGroup = normalizeGroupValue(group);
      if (savedGroup === nextGroup) return [];
      return [{
        slide_id,
        target_playlist_group: group || null,
      }];
    })
  ), [pendingSlideGroupOverrides, slideItems]);

  const savePendingSlideGroupChanges = () => {
    if (pendingSlideGroupUpdates.length === 0) return;
    slideGroupMutation.mutate(pendingSlideGroupUpdates);
  };

  const setSegmentGroupOverride = (segment: OrganizationSegmentCatalogItem, value: string) => {
    setPendingGroupOverrides((current) => {
      const next = { ...current };
      if (normalizeGroupValue(value) === normalizeGroupValue(segment.playlist_group)) {
        delete next[segment.segment_id];
      } else {
        next[segment.segment_id] = value;
      }
      return next;
    });
  };

  const stageSelectedSegmentGroup = (group: string) => {
    const selected = selectedSegmentIds
      .map((segmentId) => segments.find((segment) => segment.segment_id === segmentId))
      .filter(Boolean) as OrganizationSegmentCatalogItem[];
    if (selected.length === 0) return;
    setPendingGroupOverrides((current) => {
      const next = { ...current };
      selected.forEach((segment) => {
        if (normalizeGroupValue(group) === normalizeGroupValue(segment.playlist_group)) {
          delete next[segment.segment_id];
        } else {
          next[segment.segment_id] = group;
        }
      });
      return next;
    });
    setSegmentBulkGroupDraft('');
  };

  const setSlideGroupOverride = (item: SlideCatalogItem, value: string) => {
    setPendingSlideGroupOverrides((current) => {
      const next = { ...current };
      if (normalizeGroupValue(value) === normalizeGroupValue(item.slide.playlist_group)) {
        delete next[item.slideId];
      } else {
        next[item.slideId] = value;
      }
      return next;
    });
  };

  const stageSelectedSlideGroup = (group: string) => {
    const selected = selectedSlideIds
      .map((slideId) => slideItems.find((item) => item.slideId === slideId))
      .filter(Boolean) as SlideCatalogItem[];
    if (selected.length === 0) return;
    setPendingSlideGroupOverrides((current) => {
      const next = { ...current };
      selected.forEach((item) => {
        if (normalizeGroupValue(group) === normalizeGroupValue(item.slide.playlist_group)) {
          delete next[item.slideId];
        } else {
          next[item.slideId] = group;
        }
      });
      return next;
    });
    setSlideBulkGroupDraft('');
  };

  const toggleSelectedSegment = (segmentId: string) => {
    setSelectedSegmentIds((current) =>
      current.includes(segmentId)
        ? current.filter((id) => id !== segmentId)
        : [...current, segmentId],
    );
  };

  const setSlideSelected = (slideId: string, selected: boolean) => {
    setSelectedSlideIds((current) => {
      if (selected) {
        return current.includes(slideId) ? current : [...current, slideId];
      }
      return current.filter((id) => id !== slideId);
    });
  };

  const toggleVisibleSlides = (selected: boolean) => {
    if (visibleSlideIds.length === 0) return;
    setSelectedSlideIds((current) => {
      if (!selected) {
        return current.filter((slideId) => !visibleSlideIds.includes(slideId));
      }
      const next = new Set(current);
      visibleSlideIds.forEach((slideId) => next.add(slideId));
      return Array.from(next);
    });
  };

  if (!currentOrganization) {
    return (
      <div className="flex min-h-0 flex-col">
        <div className="flex h-full items-center justify-center">
          <div className="text-center text-subtle">
            <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin" />
            <p>Loading organization...</p>
          </div>
        </div>
      </div>
    );
  }

  const clearPendingChanges = () => {
    setPendingSegmentActions({});
    setPendingGroupOverrides({});
  };

  const renderSourceFilter = () => {
    const isSlides = assetMode === 'slides';
    const SourceIcon = isSlides ? FileText : FileVideo;

    return (
      <Popover open={isSourceFilterOpen} onOpenChange={setIsSourceFilterOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="h-9 w-full justify-between gap-2 rounded-lg border-[hsl(var(--input))] bg-card text-sm font-medium"
          >
            <span className="flex min-w-0 items-center gap-2">
              <SourceIcon className="h-4 w-4 text-muted-foreground" />
              <span className="truncate">
                {selectedSourceVideoIds.length === 0
                  ? 'Source'
                  : `${selectedSourceVideoIds.length} source${selectedSourceVideoIds.length === 1 ? '' : 's'}`}
              </span>
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[340px] p-0">
          <Command>
            <CommandInput placeholder={isSlides ? 'Search source decks' : 'Search source videos'} />
            <CommandList>
              <CommandEmpty>No sources found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="all sources"
                  onSelect={() => {
                    setSelectedSourceVideoIds([]);
                    setSelectedSegmentIds([]);
                    setSelectedSlideIds([]);
                    syncAssetUrl(assetsTab, [], assetMode);
                  }}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${
                      selectedSourceVideoIds.length === 0 ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                  <span className="flex-1">All sources</span>
                  <span className="text-xs tabular-nums text-muted-foreground">{sourceItemCount}</span>
                </CommandItem>
                {sourceOptions.map((source) => {
                  const selected = selectedSourceVideoIds.includes(source.videoId);
                  return (
                    <CommandItem
                      key={source.videoId}
                      value={`${source.label} ${source.videoId}`}
                      onSelect={() => {
                        const nextSources = selected
                          ? selectedSourceVideoIds.filter((videoId) => videoId !== source.videoId)
                          : [...selectedSourceVideoIds, source.videoId];
                        setSelectedSourceVideoIds(nextSources);
                        setSelectedSegmentIds([]);
                        setSelectedSlideIds([]);
                        syncAssetUrl(assetsTab, nextSources, assetMode);
                      }}
                    >
                      <Check className={`mr-2 h-4 w-4 ${selected ? 'opacity-100' : 'opacity-0'}`} />
                      <span className="min-w-0 flex-1 truncate">{source.label}</span>
                      {!isSlides && source.staged > 0 && (
                        <Badge variant="secondary" className="ml-2 h-5 text-[10px]">
                          {source.staged} staged
                        </Badge>
                      )}
                      <span className="ml-2 text-xs tabular-nums text-muted-foreground">{source.count}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  };

  const renderGroupFilter = (mode: AssetMode) => {
    const isSegments = mode === 'segments';
    const groups = isSegments ? availableSegmentGroups : availableSlideGroups;
    const selectedGroups = isSegments ? selectedSegmentGroups : selectedSlideGroups;
    const setSelectedGroups = isSegments ? setSelectedSegmentGroups : setSelectedSlideGroups;
    const isOpen = isSegments ? isSegmentGroupFilterOpen : isSlideGroupFilterOpen;
    const setIsOpen = isSegments ? setIsSegmentGroupFilterOpen : setIsSlideGroupFilterOpen;
    const label = isSegments ? 'Segment Group' : 'Slide Group';

    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="h-9 w-full justify-between gap-2 rounded-lg border-[hsl(var(--input))] bg-card text-sm font-medium"
          >
            <span className="flex min-w-0 items-center gap-2">
              <Tags className="h-4 w-4 text-muted-foreground" />
              <span className="truncate">
                {selectedGroups.length === 0
                  ? label
                  : `${selectedGroups.length} group${selectedGroups.length === 1 ? '' : 's'}`}
              </span>
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[300px] p-0">
          <Command>
            <CommandInput placeholder={`Search ${label.toLowerCase()}s`} />
            <CommandList>
              <CommandEmpty>No groups found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value={`all ${label.toLowerCase()}s`}
                  onSelect={() => {
                    setSelectedGroups([]);
                    if (isSegments) {
                      setSelectedSegmentIds([]);
                    } else {
                      setSelectedSlideIds([]);
                    }
                  }}
                >
                  <Check className={`mr-2 h-4 w-4 ${selectedGroups.length === 0 ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="flex-1">All groups</span>
                </CommandItem>
                {groups.map((group) => {
                  const selected = selectedGroups.includes(group);
                  return (
                    <CommandItem
                      key={group}
                      value={group}
                      onSelect={() => {
                        const nextGroups = selected
                          ? selectedGroups.filter((candidate) => candidate !== group)
                          : [...selectedGroups, group];
                        setSelectedGroups(nextGroups);
                        if (isSegments) {
                          setSelectedSegmentIds([]);
                        } else {
                          setSelectedSlideIds([]);
                        }
                      }}
                    >
                      <Check className={`mr-2 h-4 w-4 ${selected ? 'opacity-100' : 'opacity-0'}`} />
                      <span className="min-w-0 flex-1 truncate">{group}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  };

  const renderAddSourceMenu = (buttonClassName = 'h-9 gap-2 rounded-lg') => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" className={buttonClassName}>
          <Plus className="h-3.5 w-3.5" />
          Add Asset
          <ChevronDown className="h-3.5 w-3.5 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={() => setIsPresentationUploadDialogOpen(true)}>
          <Upload className="mr-2 h-3.5 w-3.5" />
          Upload Deck
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setIsUploadDialogOpen(true)}>
          <FileVideo className="mr-2 h-3.5 w-3.5" />
          Upload Capture
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => window.location.assign('/demo-studio')}>
          <Video className="mr-2 h-3.5 w-3.5" />
          New Capture
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const renderTabs = () => {
    const counts = assetMode === 'slides' ? slideTabCounts : segmentTabCounts;
    const tabs: Array<{ key: AssetsTab; icon: React.ElementType }> = [
      { key: 'all', icon: Rows3 },
      { key: 'active', icon: CheckCircle2 },
      { key: 'staged', icon: Clock },
      { key: 'library', icon: Archive },
      { key: 'discard', icon: CircleSlash },
    ];

    return (
      <div className="grid gap-2 md:grid-cols-5">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const selected = assetsTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => {
                setAssetsTab(tab.key);
                setSelectedSegmentIds([]);
                setSelectedSlideIds([]);
                syncAssetUrl(tab.key, selectedSourceVideoIds, assetMode);
              }}
              className={`flex items-center justify-between rounded-[8px] border px-3 py-2 text-left transition-colors ${
                selected
                  ? 'border-foreground/30 bg-card shadow-sm'
                  : 'border-[hsl(var(--input))] bg-secondary hover:bg-card'
              }`}
            >
              <span className="flex min-w-0 items-center gap-2">
                <Icon
                  className={`h-4 w-4 ${
                    selected ? 'text-foreground' : 'text-muted-foreground'
                  }`}
                />
                <span className="truncate text-sm font-medium">{tabLabels[tab.key]}</span>
              </span>
              <span className="text-sm font-semibold tabular-nums">{counts[tab.key]}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderAssetModeTabs = () => {
    const slideCount =
      assetMode === 'slides' || slideItems.length > 0
        ? slideItems.length
        : decks.reduce((total, deck) => total + (deck.total_slides || 0), 0);
    const tabs: Array<{ key: AssetMode; label: string; count: number; icon: React.ElementType }> = [
      { key: 'segments', label: 'Segments', count: segments.length, icon: Rows3 },
      { key: 'slides', label: 'Slides', count: slideCount, icon: FileText },
    ];

    return (
      <div className="grid min-w-0 flex-1 grid-cols-2 gap-2">
        {tabs.map((tab) => {
          const selected = assetMode === tab.key;
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setAssetModeAndUrl(tab.key)}
              className={`flex h-10 items-center justify-between gap-3 rounded-[8px] border px-3 transition-colors ${
                selected
                  ? 'border-foreground/30 bg-card text-foreground shadow-sm'
                  : 'border-[hsl(var(--input))] bg-secondary text-muted-foreground hover:bg-card'
              }`}
            >
              <span className="flex min-w-0 items-center gap-2">
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate text-sm font-medium">{tab.label}</span>
              </span>
              <span className="text-sm font-semibold tabular-nums">{tab.count}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderDeckRow = (deck: PresentationDeck) => {
    const ready = isDeckReady(deck);
    const isActive = !!deck.is_active;
    const isThisDeckMutating =
      (activateDeckMutation.isPending && activateDeckMutation.variables === deck.deck_id) ||
      (deactivateDeckMutation.isPending && deactivateDeckMutation.variables === deck.deck_id);
    const isAnyDeckMutating = activateDeckMutation.isPending || deactivateDeckMutation.isPending;

    return (
      <div
        key={deck.deck_id}
        className="flex items-center gap-3 rounded-[8px] border border-[hsl(var(--input))] bg-secondary p-3"
      >
        <button
          type="button"
          className={`flex min-w-0 flex-1 items-center gap-3 text-left ${
            ready ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'
          }`}
          onClick={() => ready && navigate(`/content-library/presentation/${deck.deck_id}`)}
        >
          <div className="flex h-12 w-16 shrink-0 items-center justify-center rounded-md border border-[hsl(var(--input))] bg-background">
            <FileText className="h-5 w-5 text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">
              {deck.title || deck.filename}
            </p>
            <p className="mt-1 text-[12px] text-muted-foreground">
              {deck.total_slides} slide{deck.total_slides === 1 ? '' : 's'} | {new Date(deck.created_at).toLocaleDateString()}
            </p>
          </div>
        </button>
        <Badge variant="outline" className={`h-6 text-[11px] ${isActive ? 'text-emerald-600' : ''}`}>
          {isActive ? 'Active' : 'Library'}
        </Badge>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          disabled={isAnyDeckMutating}
          onClick={() => {
            if (isActive) {
              deactivateDeckMutation.mutate(deck.deck_id);
            } else {
              activateDeckMutation.mutate(deck.deck_id);
            }
          }}
        >
          {isThisDeckMutating ? <Loader2 className="h-3 w-3 animate-spin" /> : isActive ? 'Remove' : 'Add'}
        </Button>
      </div>
    );
  };

  const renderVideoSourceRow = (video: SourceVideo) => {
    const stats = sourceVideoStats.get(video.video_id) || { segmentCount: 0, missingClipCount: 0 };
    const isReadyForManualClipping = video.status === 'ready' || video.status === 'pending_segmentation';
    const needsSegments = isReadyForManualClipping && stats.segmentCount === 0;
    const needsClipAssets = stats.segmentCount > 1 && stats.missingClipCount > 0;
    const isGeneratingClips =
      generateClipsMutation.isPending && generateClipsMutation.variables === video.video_id;
    const canOpenVideo = video.status === 'ready' || video.status === 'pending_segmentation';

    return (
      <div
        key={video.video_id}
        className="flex items-center gap-3 rounded-[8px] border border-[hsl(var(--input))] bg-secondary p-3"
      >
        <button
          type="button"
          className={`flex min-w-0 flex-1 items-center gap-3 text-left ${
            canOpenVideo ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'
          }`}
          onClick={() => canOpenVideo && navigate(`/content-library/video/${video.video_id}`)}
        >
          <div className="flex h-12 w-16 shrink-0 items-center justify-center rounded-md border border-[hsl(var(--input))] bg-background">
            <Play className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{video.title || video.filename}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {stats.segmentCount > 0
                ? `${stats.segmentCount} segment${stats.segmentCount === 1 ? '' : 's'}`
                : 'No segments'}
              {' | '}
              {new Date(video.created_at).toLocaleDateString()}
            </p>
          </div>
        </button>

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {needsSegments ? (
            <Badge variant="outline" className="h-6 text-[11px] text-amber-600">
              Needs segments
            </Badge>
          ) : needsClipAssets ? (
            <Badge variant="outline" className="h-6 text-[11px] text-amber-600">
              {stats.missingClipCount} clip{stats.missingClipCount === 1 ? '' : 's'} missing
            </Badge>
          ) : (
            <Badge variant="outline" className="h-6 text-[11px]">
              {video.status}
            </Badge>
          )}

          {needsSegments && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 text-xs"
              onClick={() => navigate(`/content-library/video/${video.video_id}`)}
            >
              <Scissors className="h-3 w-3" />
              Clip manually
            </Button>
          )}

          {needsClipAssets && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 text-xs"
              disabled={generateClipsMutation.isPending}
              onClick={() => generateClipsMutation.mutate(video.video_id)}
            >
              {isGeneratingClips ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <FileVideo className="h-3 w-3" />
              )}
              Generate clips
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderSourceSetupQueue = () => {
    if (assetMode !== 'segments' || sourceVideosNeedingSetup.length === 0) return null;

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Scissors className="h-4 w-4 text-muted-foreground" />
              Sources needing setup
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Clip raw captures manually, or generate missing clip assets for segmented videos.
            </p>
          </div>
        </div>
        <div className="grid gap-2 lg:grid-cols-2">
          {sourceVideosNeedingSetup.map(renderVideoSourceRow)}
        </div>
      </div>
    );
  };

  const renderDecisionButtons = (segment: OrganizationSegmentCatalogItem, compact = false) => {
    const planned = getPlannedDecision(segment);
    const decisions: SegmentDecision[] = segment.matched_segment
      ? ['activate', 'replace_matched', 'library_only', 'ignore']
      : ['activate', 'library_only', 'ignore'];

    return (
      <div className={`flex flex-wrap gap-1.5 ${compact ? 'justify-end' : ''}`}>
        {decisions.map((decision) => (
          <Button
            key={decision}
            type="button"
            size="sm"
            variant={planned === decision ? 'default' : 'outline'}
            className="h-7 px-2 text-[11px]"
            disabled={segmentCatalogMutation.isPending}
            onClick={() => setSegmentDecision(segment, decision)}
          >
            {decisionCopy[decision]}
          </Button>
        ))}
      </div>
    );
  };

  const renderSegmentRow = (segment: OrganizationSegmentCatalogItem) => {
    const isSelected = selectedSegment?.segment_id === segment.segment_id;
    const isChecked = selectedSegmentIds.includes(segment.segment_id);
    const summary = truncateText(segment.summary, 260);
    const pendingLabel = getPendingLabel(segment);
    const savedStatus = segment.catalog_status;
    const plannedDecision = getPlannedDecision(segment);
    const effectiveGroup = getEffectiveSegmentGroup(segment);
    const groupChanged = pendingGroupOverrides[segment.segment_id] !== undefined
      && normalizeGroupValue(segment.playlist_group) !== effectiveGroup;

    return (
      <div
        key={segment.segment_id}
        className={`group rounded-[8px] border transition-colors ${
          isSelected
            ? 'border-foreground/30 bg-card shadow-sm'
            : 'border-[hsl(var(--input))] bg-secondary hover:bg-card'
        } ${segment.catalog_status === 'ignored' ? 'opacity-80' : ''}`}
      >
        <div className="flex gap-3 p-3">
          <Checkbox
            checked={isChecked}
            onCheckedChange={() => toggleSelectedSegment(segment.segment_id)}
            className="mt-8"
            aria-label={`Select ${getSegmentTitle(segment)}`}
          />
          <button
            type="button"
            className="flex min-w-0 flex-1 gap-3 text-left"
            onClick={() => setSelectedSegmentId(segment.segment_id)}
          >
            <div className="relative h-[74px] w-[120px] shrink-0 overflow-hidden rounded-md bg-zinc-900">
              {segment.thumbnail_url ? (
                <img
                  src={segment.thumbnail_url}
                  alt={getSegmentTitle(segment)}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <Play className="h-4 w-4 text-zinc-500" />
                </div>
              )}
              <span className="absolute bottom-1 right-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-white">
                {formatTime(segment.duration)}
              </span>
            </div>
            <div className="min-w-0 flex-1 py-0.5">
              <div className="flex flex-wrap items-center gap-2">
                <p className="truncate text-sm font-medium text-foreground">
                  {getSegmentTitle(segment)}
                </p>
                <Badge variant={savedStatus === 'active' ? 'secondary' : 'outline'} className="h-5 text-[10px]">
                  {catalogStatusCopy[savedStatus]}
                </Badge>
                {segment.recommended_action && (
                  <Badge variant="outline" className="h-5 text-[10px]">
                    {recommendationCopy[segment.recommended_action] || segment.recommended_action}
                  </Badge>
                )}
                {pendingLabel && (
                  <Badge variant="outline" className="h-5 text-[10px] text-amber-600">
                    Unsaved: {pendingLabel}
                  </Badge>
                )}
                {groupChanged && (
                  <Badge variant="outline" className="h-5 text-[10px] text-amber-600">
                    Unsaved group
                  </Badge>
                )}
              </div>
              <p className="mt-1 truncate text-[12px] text-muted-foreground">
                {getSegmentSource(segment)} | {formatTime(segment.start_time)}-{formatTime(segment.end_time)}
              </p>
              {summary ? (
                <p className="mt-1 line-clamp-2 text-xs text-subtle">{summary}</p>
              ) : (
                <p className="mt-1 text-xs italic text-muted-foreground">No summary yet</p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {effectiveGroup && (
                  <Badge variant="outline" className="h-5 text-[10px]">
                    {effectiveGroup}
                  </Badge>
                )}
                {segment.content_type && (
                  <Badge variant="outline" className="h-5 text-[10px]">
                    {segment.content_type}
                  </Badge>
                )}
                {segment.tags.slice(0, 2).map((tag) => (
                  <Badge key={tag} variant="secondary" className="h-5 text-[10px]">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          </button>
          <div
            className={`flex shrink-0 flex-col items-end gap-2 ${
              segment.catalog_status === 'pending'
                ? 'w-[148px]'
                : segment.catalog_status === 'active'
                  ? 'w-12'
                  : 'w-[112px]'
            }`}
          >
            {segment.catalog_status === 'active' ? (
              <Switch
                checked={plannedDecision ? plannedDecision === 'activate' : true}
                disabled={segmentCatalogMutation.isPending}
                onCheckedChange={(checked) =>
                  setSegmentDecision(segment, checked ? 'activate' : 'library_only')
                }
                aria-label="Add or remove from Agent"
              />
            ) : segment.catalog_status === 'pending' ? (
              renderDecisionButtons(segment, true)
            ) : (
              <Button
                variant={plannedDecision === 'activate' ? 'default' : 'outline'}
                size="sm"
                className="h-7 text-xs"
                disabled={segmentCatalogMutation.isPending}
                onClick={() => setSegmentDecision(segment, 'activate')}
              >
                Add to Agent
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderMatchedSegmentPlayback = (
    segment: OrganizationSegmentCatalogItem,
    matchedSegment: NonNullable<OrganizationSegmentCatalogItem['matched_segment']>,
  ) => {
    const startTime = segment.playback_start_time || matchedSegment.start_time || 0;
    const endTime = segment.playback_end_time || matchedSegment.end_time || null;
    const title = matchedSegment.label || getSegmentTitle(segment);
    const source = matchedSegment.video_title || getSegmentSource(segment);
    const summary = segment.summary || segment.description || matchedSegment.description;

    return (
      <div className="space-y-3">
        <div className="overflow-hidden rounded-md border border-[hsl(var(--input))] bg-zinc-950">
          {segment.playback_type === 'dom_recording' && segment.dom_recording_url ? (
            <div className="w-full" data-rrweb-container>
              <div className="rrweb-shell">
                <DomRecordingPlayer
                  key={`matched-${segment.segment_id}`}
                  sourceUrl={segment.dom_recording_url}
                  audioUrl={segment.dom_audio_url}
                  fillContainer
                  onPlayerReady={(api) => api.goto(startTime)}
                />
              </div>
            </div>
          ) : segment.media_url ? (
            <video
              key={`matched-${segment.segment_id}`}
              controls
              playsInline
              preload="metadata"
              src={segment.media_url}
              className="aspect-video w-full bg-black object-contain"
              onLoadedMetadata={(event) => {
                event.currentTarget.currentTime = startTime;
              }}
              onTimeUpdate={(event) => {
                if (endTime && event.currentTarget.currentTime > endTime) {
                  event.currentTarget.pause();
                }
              }}
            />
          ) : matchedSegment.thumbnail_url ? (
            <img
              src={matchedSegment.thumbnail_url}
              alt={title}
              className="aspect-video w-full object-cover"
            />
          ) : (
            <div className="flex aspect-video items-center justify-center text-xs text-zinc-500">
              No preview
            </div>
          )}
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-medium">{title}</p>
            {segment.catalog_status && (
              <Badge variant="outline" className="h-5 text-[10px]">
                {catalogStatusCopy[segment.catalog_status]}
              </Badge>
            )}
          </div>
          <p className="mt-1 truncate text-xs text-muted-foreground">
            {source} | {formatTime(matchedSegment.start_time)}-{formatTime(matchedSegment.end_time)}
          </p>
          {summary && <p className="mt-2 text-xs text-muted-foreground">{summary}</p>}
        </div>
      </div>
    );
  };

  const renderMetadataRow = (label: string, value?: React.ReactNode) => {
    if (value == null || value === '') return null;
    return (
      <div className="grid gap-1 border-t border-[hsl(var(--input))] py-2 first:border-t-0 sm:grid-cols-[132px_minmax(0,1fr)]">
        <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
        <dd className="min-w-0 text-xs text-foreground">{value}</dd>
      </div>
    );
  };

  const renderSegmentMetadata = (segment: OrganizationSegmentCatalogItem) => {
    const contextChanged = contextDraft !== (segment.ai_context || '');
    const segmentGroupChanged = pendingGroupOverrides[segment.segment_id] !== undefined;

    return (
      <Collapsible className="rounded-[8px] border border-[hsl(var(--input))] bg-secondary">
        <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left">
          <div className="min-w-0">
            <p className="text-sm font-medium">Metadata</p>
            <p className="truncate text-xs text-muted-foreground">
              Context and source details
            </p>
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="border-t border-[hsl(var(--input))] p-3">
          <div className="mb-3 flex items-center justify-between gap-3">
            <Label htmlFor={`context-${segment.segment_id}`} className="text-xs text-muted-foreground">
              Context
            </Label>
            <Button
              size="sm"
              className="h-7 text-xs"
              disabled={!contextChanged || updateSegmentContextMutation.isPending}
              onClick={() => updateSegmentContextMutation.mutate({ segment, context: contextDraft })}
            >
              {updateSegmentContextMutation.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                'Save context'
              )}
            </Button>
          </div>

          <Textarea
            id={`context-${segment.segment_id}`}
            value={contextDraft}
            placeholder="Add guidance for how the agent should use this segment."
            className="min-h-[88px] resize-y text-sm"
            disabled={updateSegmentContextMutation.isPending}
            onChange={(event) => setContextDraft(event.target.value)}
          />

          <dl className="mt-3">
            {renderMetadataRow('Content type', segment.content_type)}
            <div className="grid gap-1 border-t border-[hsl(var(--input))] py-2 first:border-t-0 sm:grid-cols-[132px_minmax(0,1fr)]">
              <dt className="text-xs font-medium text-muted-foreground">Segment Group</dt>
              <dd className="min-w-0">
                <div className="flex items-center gap-2">
                  <Input
                    value={
                      pendingGroupOverrides[segment.segment_id] ??
                      segment.playlist_group ??
                      ''
                    }
                    list="asset-segment-groups"
                    className="h-8 text-xs"
                    disabled={segmentCatalogMutation.isPending}
                    onChange={(event) => setSegmentGroupOverride(segment, event.target.value)}
                  />
                  {segmentGroupChanged && (
                    <Badge variant="outline" className="shrink-0 text-amber-600">
                      Unsaved
                    </Badge>
                  )}
                </div>
              </dd>
            </div>
            {renderMetadataRow('Playlist policy', segment.playlist_policy)}
            {renderMetadataRow('Segment type', segment.segment_type)}
            {renderMetadataRow('Playback', segment.playback_type)}
            {renderMetadataRow('Visual description', segment.visual_description)}
            {renderMetadataRow('Transcript', segment.speech_transcript)}
            {renderMetadataRow('Scene text', segment.scene_text)}
            {renderMetadataRow('Segment ID', segment.segment_ref || segment.segment_id)}
            {renderMetadataRow('Source ID', segment.video_id)}
          </dl>
        </CollapsibleContent>
      </Collapsible>
    );
  };

  const renderSegmentPlayback = () => {
    if (!selectedSegment) {
      return (
        <div className="flex min-h-[420px] items-center justify-center rounded-[8px] border border-[hsl(var(--input))] bg-secondary">
          <div className="text-center text-sm text-muted-foreground">
            <MonitorPlay className="mx-auto mb-2 h-7 w-7 text-subtle" />
            Select a segment to preview it.
          </div>
        </div>
      );
    }

    const selectedPendingLabel = getPendingLabel(selectedSegment);
    const plannedDecision = getPlannedDecision(selectedSegment);
    const matchedCatalogSegment = selectedSegment.matched_segment
      ? segments.find((segment) => segment.segment_id === selectedSegment.matched_segment?.segment_id)
      : null;

    return (
      <div className="overflow-hidden rounded-[8px] border border-[hsl(var(--input))] bg-card">
        <div className="border-b border-[hsl(var(--input))] bg-secondary px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">
                {getSegmentTitle(selectedSegment)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {getSegmentSource(selectedSegment)} | {formatTime(selectedSegment.start_time)}-{formatTime(selectedSegment.end_time)}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              {selectedPendingLabel && (
                <Badge variant="outline" className="text-amber-600">
                  Unsaved
                </Badge>
              )}
              <Badge variant={selectedSegment.catalog_status === 'active' ? 'secondary' : 'outline'}>
                {catalogStatusCopy[selectedSegment.catalog_status]}
              </Badge>
            </div>
          </div>
        </div>

        {selectedSegment.playback_type === 'dom_recording' && selectedSegment.dom_recording_url ? (
          <div className="w-full" data-rrweb-container>
            <div className="rrweb-shell">
              <DomRecordingPlayer
                key={selectedSegment.segment_id}
                sourceUrl={selectedSegment.dom_recording_url}
                audioUrl={selectedSegment.dom_audio_url}
                fillContainer
                onPlayerReady={(api) => api.goto(selectedSegment.playback_start_time || 0)}
              />
            </div>
          </div>
        ) : selectedSegment.media_url ? (
          <video
            key={selectedSegment.segment_id}
            ref={playbackVideoRef}
            controls
            playsInline
            preload="metadata"
            src={selectedSegment.media_url}
            className="aspect-video w-full bg-black object-contain"
            onLoadedMetadata={(event) => {
              event.currentTarget.currentTime = selectedSegment.playback_start_time || 0;
            }}
            onTimeUpdate={(event) => {
              const endTime = selectedSegment.playback_end_time;
              if (endTime && event.currentTarget.currentTime > endTime) {
                event.currentTarget.pause();
              }
            }}
          />
        ) : (
          <div className="flex aspect-video items-center justify-center bg-zinc-950 text-sm text-zinc-400">
            Playback source not available
          </div>
        )}

        <div className="space-y-4 p-4">
          {selectedSegment.catalog_status === 'pending' && (
            <div className="rounded-[8px] border border-[hsl(var(--input))] bg-secondary p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Decision</p>
                  <p className="text-sm font-medium">{plannedDecision ? decisionCopy[plannedDecision] : 'Choose action'}</p>
                </div>
                {typeof selectedSegment.confidence === 'number' && (
                  <Badge variant="outline">{Math.round(selectedSegment.confidence * 100)}% confidence</Badge>
                )}
              </div>
              {renderDecisionButtons(selectedSegment)}
            </div>
          )}

          <div>
            <p className="text-sm text-foreground">
              {selectedSegment.summary || selectedSegment.description || 'No segment summary yet.'}
            </p>
            {selectedSegment.reason && (
              <p className="mt-2 text-xs text-muted-foreground">{selectedSegment.reason}</p>
            )}
          </div>

          {renderSegmentMetadata(selectedSegment)}

          {selectedSegment.matched_segment && (
            <div className="rounded-[8px] border border-[hsl(var(--input))] bg-secondary p-3">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-medium">Existing segment being replaced</p>
                </div>
                <Badge variant="outline" className="h-5 text-[10px]">
                  Current match
                </Badge>
              </div>
              {matchedCatalogSegment
                ? renderMatchedSegmentPlayback(matchedCatalogSegment, selectedSegment.matched_segment)
                : (
                  <div className="grid gap-3 sm:grid-cols-[104px_minmax(0,1fr)]">
                    <div className="aspect-video overflow-hidden rounded-md bg-zinc-900">
                      {selectedSegment.matched_segment.thumbnail_url ? (
                        <img
                          src={selectedSegment.matched_segment.thumbnail_url}
                          alt={selectedSegment.matched_segment.label || 'Matched segment'}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-xs text-zinc-500">
                          No preview
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">
                        {selectedSegment.matched_segment.label || 'Untitled segment'}
                      </p>
                      <p className="mt-1 truncate text-xs text-muted-foreground">
                        {selectedSegment.matched_segment.video_title || 'Existing source'}
                      </p>
                      <p className="mt-1 text-xs tabular-nums text-muted-foreground">
                        {formatTime(selectedSegment.matched_segment.start_time)}-
                        {formatTime(selectedSegment.matched_segment.end_time)}
                      </p>
                    </div>
                  </div>
                )}
            </div>
          )}

          <div className="flex flex-wrap gap-1.5">
            {selectedSegment.segment_ref && <Badge variant="outline">{selectedSegment.segment_ref}</Badge>}
            {getEffectiveSegmentGroup(selectedSegment) && (
              <Badge variant="outline">{getEffectiveSegmentGroup(selectedSegment)}</Badge>
            )}
            {selectedSegment.content_type && <Badge variant="outline">{selectedSegment.content_type}</Badge>}
            {selectedSegment.recommended_action && (
              <Badge variant="outline">
                {recommendationCopy[selectedSegment.recommended_action] || selectedSegment.recommended_action}
              </Badge>
            )}
            {selectedSegment.tags.map((tag) => (
              <Badge key={tag} variant="secondary">{tag}</Badge>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderSaveBar = () => {
    const showStagedSave = assetsTab === 'staged' && visiblePendingUpdates.length > 0;
    const showExplicitSave = explicitPendingCount > 0;
    const hasManualChanges = showExplicitSave;
    if (!showStagedSave && !showExplicitSave) return null;

    return (
      <div className="rounded-[8px] border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900/50 dark:bg-amber-950/30">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <p className="text-sm font-medium text-amber-950 dark:text-amber-100">
              {hasManualChanges
                ? `${pendingSaveCount} decision${pendingSaveCount === 1 ? '' : 's'} ready to save`
                : `${pendingSaveCount} suggested decision${pendingSaveCount === 1 ? '' : 's'} ready`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="h-8"
              disabled={segmentCatalogMutation.isPending}
              onClick={savePendingSegmentChanges}
            >
              {segmentCatalogMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                hasManualChanges ? 'Save changes' : 'Apply suggestions'
              )}
            </Button>
            {showExplicitSave && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-amber-900 hover:text-amber-950 dark:text-amber-100"
                disabled={segmentCatalogMutation.isPending}
                onClick={clearPendingChanges}
              >
                Discard changes
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSlideGroupSaveBar = () => {
    if (pendingSlideGroupUpdates.length === 0) return null;

    return (
      <div className="rounded-[8px] border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900/50 dark:bg-amber-950/30">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <p className="text-sm font-medium text-amber-950 dark:text-amber-100">
            {pendingSlideGroupUpdates.length} slide group change{pendingSlideGroupUpdates.length === 1 ? '' : 's'} ready to save
          </p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="h-8"
              disabled={slideGroupMutation.isPending}
              onClick={savePendingSlideGroupChanges}
            >
              {slideGroupMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                'Save changes'
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-amber-900 hover:text-amber-950 dark:text-amber-100"
              disabled={slideGroupMutation.isPending}
              onClick={() => setPendingSlideGroupOverrides({})}
            >
              Discard changes
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderWorkspace = () => {
    const railTitle = isSearchMode
      ? 'Search Results'
      : assetsTab === 'library'
        ? 'Segment Library'
        : `${tabLabels[assetsTab]} Segments`;
    const railCount = isSearchMode
      ? `${filteredSegments.length} match${filteredSegments.length === 1 ? '' : 'es'}`
      : `${filteredSegments.length} visible`;
    const emptyTitle = isSearchMode
      ? 'No matching segments'
      : assetsTab === 'all'
        ? 'No segments found'
        : selectedSourceVideoIds.length > 0 && assetsTab === 'staged'
          ? 'No staged segments for this source'
          : `No ${tabLabels[assetsTab].toLowerCase()} segments`;
    const emptyDescription = isSearchMode || assetsTab === 'all'
      ? 'Try clearing search or source filters.'
      : assetsTab === 'staged'
        ? 'Processed captures with pending suggestions appear here.'
        : 'Try clearing search or source filters.';

    return (
      <div className="grid min-h-[620px] items-start gap-4 xl:grid-cols-[minmax(460px,0.96fr)_minmax(460px,1.04fr)]">
        <div className="flex min-h-[620px] min-w-0 flex-col overflow-hidden rounded-[8px] border border-[hsl(var(--input))] bg-background xl:sticky xl:top-4 xl:max-h-[calc(100vh-7rem)] xl:self-start">
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[hsl(var(--input))] px-4 py-3">
            <div>
              <p className="text-sm font-semibold">{railTitle}</p>
              <p className="text-xs text-muted-foreground">
                {railCount} | {selectedSegmentIds.length} selected
                {selectedSourceVideoIds.length > 0 && selectedSourceLabel ? ` | ${selectedSourceLabel}` : ''}
              </p>
            </div>
            {selectedSegmentIds.length > 0 && (
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  disabled={segmentCatalogMutation.isPending}
                  onClick={() => stageSegments(selectedSegmentIds, 'activate')}
                >
                  Add to Agent
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  disabled={segmentCatalogMutation.isPending}
                  onClick={() => stageSegments(selectedSegmentIds, 'library_only')}
                >
                  Move to Library
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  disabled={segmentCatalogMutation.isPending}
                  onClick={() => stageSegments(selectedSegmentIds, 'ignore')}
                >
                  Discard
                </Button>
                <div className="flex items-center gap-1">
                  <Input
                    value={segmentBulkGroupDraft}
                    list="asset-segment-groups"
                    placeholder="Segment group"
                    className="h-8 w-[150px] text-xs"
                    disabled={segmentCatalogMutation.isPending}
                    onChange={(event) => setSegmentBulkGroupDraft(event.target.value)}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    disabled={segmentCatalogMutation.isPending || !segmentBulkGroupDraft.trim()}
                    onClick={() => stageSelectedSegmentGroup(segmentBulkGroupDraft)}
                  >
                    Set group
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    disabled={segmentCatalogMutation.isPending}
                    onClick={() => stageSelectedSegmentGroup('')}
                  >
                    Clear group
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8"
                  onClick={() => setSelectedSegmentIds([])}
                >
                  Clear
                </Button>
              </div>
            )}
          </div>
          <div ref={segmentsScrollRef} className="min-h-0 flex-1 overflow-y-auto p-3">
            {filteredSegments.length > 0 ? (
              <div
                style={{
                  height: segmentsVirtualizer.getTotalSize(),
                  width: '100%',
                  position: 'relative',
                }}
              >
                {segmentsVirtualizer.getVirtualItems().map((virtualRow) => {
                  const segment = filteredSegments[virtualRow.index];
                  if (!segment) return null;
                  return (
                    <div
                      key={virtualRow.key}
                      data-index={virtualRow.index}
                      ref={segmentsVirtualizer.measureElement}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualRow.start}px)`,
                        paddingBottom: 8,
                      }}
                    >
                      {renderSegmentRow(segment)}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex min-h-[360px] items-center justify-center rounded-[8px] bg-secondary">
                <div className="max-w-sm text-center text-sm text-muted-foreground">
                  <FileSearch className="mx-auto mb-2 h-7 w-7 text-subtle" />
                  <p className="font-medium text-foreground">{emptyTitle}</p>
                  <p className="mt-1 text-xs">{emptyDescription}</p>
                  {selectedSourceVideoIds.length > 0 && assetsTab === 'staged' && (
                    <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                      <Button
                        size="sm"
                        className="h-8"
                        onClick={() => {
                          setAssetsTab('all');
                          setSelectedSegmentIds([]);
                          syncAssetUrl('all', selectedSourceVideoIds);
                        }}
                      >
                        View all segments from this source
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        onClick={() => {
                          setSelectedSourceVideoIds([]);
                          setSelectedSegmentIds([]);
                          syncAssetUrl(assetsTab, []);
                        }}
                      >
                        Clear source filter
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="min-w-0 xl:sticky xl:top-4 xl:self-start">
          {renderSegmentPlayback()}
        </div>
      </div>
    );
  };

  const renderSlideRow = (item: SlideCatalogItem) => {
    const isSelected = selectedSlide?.slideId === item.slideId;
    const summary = truncateText(item.slide.content_summary || item.slide.use_case, 150);
    const slideStatus = getSlideCatalogStatus(item);
    const isChecked = selectedSlideIdSet.has(item.slideId);
    const effectiveGroup = getEffectiveSlideGroup(item);
    const groupChanged = pendingSlideGroupOverrides[item.slideId] !== undefined
      && normalizeGroupValue(item.slide.playlist_group) !== effectiveGroup;

    return (
      <div
        key={item.slideId}
        role="button"
        tabIndex={0}
        className={`group flex w-full cursor-pointer gap-3 rounded-[8px] border p-3 text-left transition-colors ${
          isSelected
            ? 'border-foreground/30 bg-card shadow-sm'
            : 'border-[hsl(var(--input))] bg-secondary hover:bg-card'
        }`}
        onClick={() => setSelectedSlideId(item.slideId)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setSelectedSlideId(item.slideId);
          }
        }}
      >
        <div
          className="flex shrink-0 items-center pt-6"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <Checkbox
            aria-label={`Select ${getSlideTitle(item.slide)}`}
            checked={isChecked}
            disabled={bulkSlideActionMutation.isPending || bulkDeleteSlidesMutation.isPending}
            onCheckedChange={(checked) => setSlideSelected(item.slideId, checked === true)}
          />
        </div>
        <div className="relative h-[74px] w-[120px] shrink-0 overflow-hidden rounded-md bg-zinc-900">
          {item.slide.image_url ? (
            <img
              src={item.slide.image_url}
              alt={getSlideTitle(item.slide)}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <FileText className="h-4 w-4 text-zinc-500" />
            </div>
          )}
          <span className="absolute bottom-1 right-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-white">
            {item.slide.slide_number}
          </span>
        </div>
        <div className="min-w-0 flex-1 py-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-medium text-foreground">
              {getSlideTitle(item.slide)}
            </p>
            <Badge variant={slideStatus === 'active' ? 'secondary' : 'outline'} className="h-5 text-[10px]">
              {catalogStatusCopy[slideStatus]}
            </Badge>
            {effectiveGroup && (
              <Badge variant="outline" className="h-5 text-[10px]">
                {effectiveGroup}
              </Badge>
            )}
            {groupChanged && (
              <Badge variant="outline" className="h-5 text-[10px] text-amber-600">
                Unsaved group
              </Badge>
            )}
          </div>
          <p className="mt-1 truncate text-[12px] text-muted-foreground">
            {getDeckTitle(item.deck)} | Slide {item.slide.slide_number}
          </p>
          {summary ? (
            <p className="mt-1 line-clamp-2 text-xs text-subtle">{summary}</p>
          ) : (
            <p className="mt-1 text-xs italic text-muted-foreground">No summary yet</p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {(item.slide.topics || []).slice(0, 3).map((topic) => (
              <Badge key={topic} variant="secondary" className="h-5 text-[10px]">
                {topic}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderSlideDetails = () => {
    if (deckSlidesLoading) {
      return (
        <div className="h-[520px] animate-pulse rounded-[8px] border border-[hsl(var(--input))] bg-secondary" />
      );
    }

    if (!selectedSlide) {
      return (
        <div className="flex min-h-[420px] items-center justify-center rounded-[8px] border border-[hsl(var(--input))] bg-secondary">
          <div className="text-center text-sm text-muted-foreground">
            <FileText className="mx-auto mb-2 h-7 w-7 text-subtle" />
            Select a slide to preview it.
          </div>
        </div>
      );
    }

    const { deck, slide } = selectedSlide;
    const ready = isDeckReady(deck);
    const slideStatus = getSlideCatalogStatus(selectedSlide);
    const canDeleteSlide = slideStatus === 'ignored';
    const isThisSlideToggling = bulkSlideActionMutation.isPending;
    const isAnySlideMutating =
      activateSlideMutation.isPending ||
      deactivateSlideMutation.isPending ||
      bulkSlideActionMutation.isPending ||
      updateSlideInfoMutation.isPending ||
      deleteSlideMutation.isPending ||
      bulkDeleteSlidesMutation.isPending ||
      slideGroupMutation.isPending;
    const slideBaselineDraft = createSlideDraft(slide);
    const slideInfoChanged = !areSlideDraftsEqual(slideDraft, slideBaselineDraft);
    const resetSlideDraft = () => {
      setSlideDraft(slideBaselineDraft);
      setIsEditingSlideInfo(false);
    };

    return (
      <div className="overflow-hidden rounded-[8px] border border-[hsl(var(--input))] bg-card">
        <div className="border-b border-[hsl(var(--input))] bg-secondary px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">
                {getSlideTitle(slide)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {getDeckTitle(deck)} | Slide {slide.slide_number}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Badge variant={slideStatus === 'active' ? 'secondary' : 'outline'}>
                {catalogStatusCopy[slideStatus]}
              </Badge>
              {getEffectiveSlideGroup(selectedSlide) && (
                <Badge variant="outline">
                  {getEffectiveSlideGroup(selectedSlide)}
                </Badge>
              )}
              {canDeleteSlide && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  disabled={isAnySlideMutating}
                  title="Delete slide"
                  aria-label="Delete slide"
                  onClick={() => setSlidePendingDeletion(selectedSlide)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="bg-zinc-950">
          {slide.image_url ? (
            <img
              src={slide.image_url}
              alt={getSlideTitle(slide)}
              className="aspect-video w-full object-contain"
            />
          ) : (
            <div className="flex aspect-video items-center justify-center text-sm text-zinc-400">
              Slide image not available
            </div>
          )}
        </div>

        <div className="space-y-4 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={!ready}
              onClick={() => navigate(`/content-library/presentation/${deck.deck_id}`)}
            >
              Open deck
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-2"
              disabled={isAnySlideMutating}
              onClick={() => setIsEditingSlideInfo(true)}
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit info
            </Button>
            {slideStatus !== 'active' && (
              <Button
                variant="default"
                size="sm"
                className="h-8"
                disabled={isAnySlideMutating}
                onClick={() =>
                  bulkSlideActionMutation.mutate({
                    slideIds: [selectedSlide.slideId],
                    catalogStatus: 'active',
                  })
                }
              >
                {isThisSlideToggling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Add to Agent'}
              </Button>
            )}
            {slideStatus !== 'library_only' && (
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                disabled={isAnySlideMutating}
                onClick={() =>
                  bulkSlideActionMutation.mutate({
                    slideIds: [selectedSlide.slideId],
                    catalogStatus: 'library_only',
                  })
                }
              >
                Move to Library
              </Button>
            )}
            {slideStatus !== 'ignored' ? (
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                disabled={isAnySlideMutating}
                onClick={() =>
                  bulkSlideActionMutation.mutate({
                    slideIds: [selectedSlide.slideId],
                    catalogStatus: 'ignored',
                  })
                }
              >
                Discard
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-2 border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
                disabled={isAnySlideMutating}
                onClick={() => setSlidePendingDeletion(selectedSlide)}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete slide
              </Button>
            )}
          </div>

          <div className="rounded-[8px] border border-[hsl(var(--input))] bg-secondary p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1 space-y-1.5">
                <Label htmlFor={`slide-group-${selectedSlide.slideId}`} className="text-xs text-muted-foreground">
                  Slide Group
                </Label>
                <Input
                  id={`slide-group-${selectedSlide.slideId}`}
                  value={
                    pendingSlideGroupOverrides[selectedSlide.slideId] ??
                    slide.playlist_group ??
                    ''
                  }
                  list="asset-slide-groups"
                  className="h-8 text-xs"
                  disabled={slideGroupMutation.isPending}
                  onChange={(event) => setSlideGroupOverride(selectedSlide, event.target.value)}
                />
              </div>
              {pendingSlideGroupOverrides[selectedSlide.slideId] !== undefined && (
                <Badge variant="outline" className="mt-5 text-amber-600">
                  Unsaved
                </Badge>
              )}
            </div>
          </div>

          {isEditingSlideInfo ? (
            <div className="rounded-[8px] border border-[hsl(var(--input))] bg-secondary p-3">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Slide information</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Edit how this slide appears in Agent Assets.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8"
                    disabled={updateSlideInfoMutation.isPending}
                    onClick={resetSlideDraft}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="h-8"
                    disabled={!slideInfoChanged || updateSlideInfoMutation.isPending}
                    onClick={() =>
                      updateSlideInfoMutation.mutate({
                        slideId: selectedSlide.slideId,
                        draft: slideDraft,
                      })
                    }
                  >
                    {updateSlideInfoMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      'Save'
                    )}
                  </Button>
                </div>
              </div>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor={`slide-title-${selectedSlide.slideId}`} className="text-xs text-muted-foreground">
                    Title
                  </Label>
                  <Input
                    id={`slide-title-${selectedSlide.slideId}`}
                    value={slideDraft.title}
                    className="h-8 text-sm"
                    disabled={updateSlideInfoMutation.isPending}
                    onChange={(event) =>
                      setSlideDraft((current) => ({ ...current, title: event.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`slide-summary-${selectedSlide.slideId}`} className="text-xs text-muted-foreground">
                    Summary
                  </Label>
                  <Textarea
                    id={`slide-summary-${selectedSlide.slideId}`}
                    value={slideDraft.contentSummary}
                    className="min-h-[88px] resize-y text-sm"
                    disabled={updateSlideInfoMutation.isPending}
                    onChange={(event) =>
                      setSlideDraft((current) => ({ ...current, contentSummary: event.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`slide-use-case-${selectedSlide.slideId}`} className="text-xs text-muted-foreground">
                    Context
                  </Label>
                  <Textarea
                    id={`slide-use-case-${selectedSlide.slideId}`}
                    value={slideDraft.useCase}
                    className="min-h-[72px] resize-y text-sm"
                    disabled={updateSlideInfoMutation.isPending}
                    onChange={(event) =>
                      setSlideDraft((current) => ({ ...current, useCase: event.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`slide-key-points-${selectedSlide.slideId}`} className="text-xs text-muted-foreground">
                    Key points
                  </Label>
                  <Textarea
                    id={`slide-key-points-${selectedSlide.slideId}`}
                    value={slideDraft.keyPoints}
                    placeholder="One key point per line"
                    className="min-h-[88px] resize-y text-sm"
                    disabled={updateSlideInfoMutation.isPending}
                    onChange={(event) =>
                      setSlideDraft((current) => ({ ...current, keyPoints: event.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`slide-visual-${selectedSlide.slideId}`} className="text-xs text-muted-foreground">
                    Visual description
                  </Label>
                  <Textarea
                    id={`slide-visual-${selectedSlide.slideId}`}
                    value={slideDraft.visualDescription}
                    className="min-h-[72px] resize-y text-sm"
                    disabled={updateSlideInfoMutation.isPending}
                    onChange={(event) =>
                      setSlideDraft((current) => ({ ...current, visualDescription: event.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`slide-topics-${selectedSlide.slideId}`} className="text-xs text-muted-foreground">
                    Topics
                  </Label>
                  <Input
                    id={`slide-topics-${selectedSlide.slideId}`}
                    value={slideDraft.topics}
                    placeholder="login, authentication, settings"
                    className="h-8 text-sm"
                    disabled={updateSlideInfoMutation.isPending}
                    onChange={(event) =>
                      setSlideDraft((current) => ({ ...current, topics: event.target.value }))
                    }
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              <div>
                <p className="text-sm text-foreground">
                  {slide.content_summary || 'No slide summary yet.'}
                </p>
                {slide.use_case && (
                  <p className="mt-2 text-xs text-muted-foreground">{slide.use_case}</p>
                )}
              </div>

              {slide.key_points && slide.key_points.length > 0 && (
                <div className="rounded-[8px] border border-[hsl(var(--input))] bg-secondary p-3">
                  <p className="text-sm font-medium">Key points</p>
                  <ul className="mt-2 space-y-1.5 text-xs text-muted-foreground">
                    {slide.key_points.map((point, index) => (
                      <li key={`${point}-${index}`}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="rounded-[8px] border border-[hsl(var(--input))] bg-secondary p-3">
                <dl>
                  {renderMetadataRow('Deck', getDeckTitle(deck))}
                  {renderMetadataRow('Slide', slide.slide_number)}
                  {renderMetadataRow('Slide status', catalogStatusCopy[slideStatus])}
                  {renderMetadataRow('Deck status', deck.is_active ? 'Active' : 'Inactive')}
                  {renderMetadataRow('Slide Group', getEffectiveSlideGroup(selectedSlide))}
                  {renderMetadataRow('Visual description', slide.visual_description)}
                  {renderMetadataRow('Context', slide.use_case)}
                  {renderMetadataRow(
                    'Topics',
                    slide.topics && slide.topics.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {slide.topics.map((topic) => (
                          <Badge key={topic} variant="secondary">{topic}</Badge>
                        ))}
                      </div>
                    ) : null,
                  )}
                  {renderMetadataRow('Slide ID', selectedSlide.slideId)}
                </dl>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderSlidesWorkspace = () => {
    const selectedSlideItems = selectedSlideIds
      .map((slideId) => slideItems.find((item) => item.slideId === slideId))
      .filter(Boolean) as SlideCatalogItem[];
    const canBulkDeleteSlides =
      assetsTab === 'discard' &&
      !isSlideSearchMode &&
      selectedSlideItems.length > 0 &&
      selectedSlideItems.every((item) => getSlideCatalogStatus(item) === 'ignored');
    const emptyTitle = slideQuery ? 'No matching slides' : `No ${tabLabels[assetsTab].toLowerCase()} slides`;
    const emptyDescription = slideQuery
      ? 'Try clearing search.'
      : assetsTab === 'staged'
        ? 'Newly uploaded deck slides will appear here for review.'
        : 'Try another lifecycle tab or add a deck.';

    return (
      <div className="grid min-h-[620px] items-start gap-4 xl:grid-cols-[minmax(460px,0.96fr)_minmax(460px,1.04fr)]">
        <div className="flex min-h-[620px] min-w-0 flex-col overflow-hidden rounded-[8px] border border-[hsl(var(--input))] bg-background xl:sticky xl:top-4 xl:max-h-[calc(100vh-7rem)] xl:self-start">
          <div className="shrink-0 border-b border-[hsl(var(--input))]">
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <Checkbox
                  id="select-visible-slides"
                  aria-label="Select all visible slides"
                  checked={slideSelectAllState}
                  disabled={filteredSlideItems.length === 0 || bulkSlideActionMutation.isPending}
                  onCheckedChange={(checked) => toggleVisibleSlides(checked === true)}
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold">Slides</p>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    {filteredSlideItems.length} visible · {activeSlideCount} active slide{activeSlideCount === 1 ? '' : 's'}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Label
                  htmlFor="select-visible-slides"
                  className="hidden cursor-pointer text-xs font-normal text-muted-foreground sm:inline"
                >
                  Select all
                </Label>
                {selectedSlideIds.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8"
                    disabled={bulkSlideActionMutation.isPending || bulkDeleteSlidesMutation.isPending}
                    onClick={() => setSelectedSlideIds([])}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
            {selectedSlideIds.length > 0 && (
              <div className="border-t border-[hsl(var(--input))] bg-secondary/50 px-4 py-3">
                <div className="flex flex-col gap-3 2xl:flex-row 2xl:items-center 2xl:justify-between">
                  <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
                    <CheckCircle2 className="h-3.5 w-3.5 text-subtle" />
                    <span>
                      {selectedSlideIds.length} slide{selectedSlideIds.length === 1 ? '' : 's'} selected
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 2xl:justify-end">
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        disabled={bulkSlideActionMutation.isPending || bulkDeleteSlidesMutation.isPending}
                        onClick={() =>
                          bulkSlideActionMutation.mutate({
                            slideIds: selectedSlideIds,
                            catalogStatus: 'active',
                          })
                        }
                      >
                        Add to Agent
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        disabled={bulkSlideActionMutation.isPending || bulkDeleteSlidesMutation.isPending}
                        onClick={() =>
                          bulkSlideActionMutation.mutate({
                            slideIds: selectedSlideIds,
                            catalogStatus: 'library_only',
                          })
                        }
                      >
                        Move to Library
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        disabled={bulkSlideActionMutation.isPending || bulkDeleteSlidesMutation.isPending}
                        onClick={() =>
                          bulkSlideActionMutation.mutate({
                            slideIds: selectedSlideIds,
                            catalogStatus: 'ignored',
                          })
                        }
                      >
                        Discard
                      </Button>
                      {canBulkDeleteSlides && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 gap-2 border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          disabled={bulkDeleteSlidesMutation.isPending}
                          onClick={() => setSlideBulkPendingDeletionIds(selectedSlideIds)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete
                        </Button>
                      )}
                    </div>
                    <div className="h-6 w-px bg-[hsl(var(--input))]" />
                    <div className="flex min-w-[220px] flex-1 flex-wrap items-center gap-2 2xl:flex-none">
                      <div className="relative min-w-[160px] flex-1 2xl:w-[180px] 2xl:flex-none">
                        <Tags className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-subtle" />
                        <Input
                          value={slideBulkGroupDraft}
                          list="asset-slide-groups"
                          placeholder="Slide group"
                          className="h-8 pl-8 text-xs"
                          disabled={slideGroupMutation.isPending}
                          onChange={(event) => setSlideBulkGroupDraft(event.target.value)}
                        />
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        disabled={slideGroupMutation.isPending || !slideBulkGroupDraft.trim()}
                        onClick={() => stageSelectedSlideGroup(slideBulkGroupDraft)}
                      >
                        Set group
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8"
                        disabled={slideGroupMutation.isPending}
                        onClick={() => stageSelectedSlideGroup('')}
                      >
                        Clear group
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div ref={slidesScrollRef} className="min-h-0 flex-1 overflow-y-auto p-3">
            {deckSlidesLoading ? (
              <div className="space-y-2" aria-busy="true" aria-label="Loading slides">
                {Array.from({ length: 6 }).map((_, index) => renderListRowSkeleton(index))}
              </div>
            ) : filteredSlideItems.length > 0 ? (
              <div
                style={{
                  height: slidesVirtualizer.getTotalSize(),
                  width: '100%',
                  position: 'relative',
                }}
              >
                {slidesVirtualizer.getVirtualItems().map((virtualRow) => {
                  const item = filteredSlideItems[virtualRow.index];
                  if (!item) return null;
                  return (
                    <div
                      key={virtualRow.key}
                      data-index={virtualRow.index}
                      ref={slidesVirtualizer.measureElement}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualRow.start}px)`,
                        paddingBottom: 8,
                      }}
                    >
                      {renderSlideRow(item)}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex min-h-[360px] items-center justify-center rounded-[8px] bg-secondary">
                <div className="max-w-sm text-center text-sm text-muted-foreground">
                  <FileSearch className="mx-auto mb-2 h-7 w-7 text-subtle" />
                  <p className="font-medium text-foreground">{emptyTitle}</p>
                  <p className="mt-1 text-xs">{emptyDescription}</p>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="min-w-0 xl:sticky xl:top-4 xl:self-start">
          {renderSlideDetails()}
        </div>
      </div>
    );
  };

  const renderActiveDecks = () => {
    if (assetsTab !== 'active' || isSearchMode) return null;
    return (
      <div className="space-y-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <FileText className="h-4 w-4 text-muted-foreground" />
            Active decks
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Decks currently available to the agent alongside active segments.
          </p>
        </div>
        {activeDecks.length > 0 ? (
          <div className="grid gap-2 lg:grid-cols-2">
            {activeDecks.map(renderDeckRow)}
          </div>
        ) : (
          <div className="rounded-[8px] border border-[hsl(var(--input))] bg-secondary p-3 text-sm text-muted-foreground">
            No active decks yet.
          </div>
        )}
      </div>
    );
  };

  const renderStoredAssets = () => {
    if (assetsTab !== 'library') return null;
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Library className="h-4 w-4 text-muted-foreground" />
              Sources
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Decks, videos, uploads, and captures that feed the agent asset catalog.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsUploadDialogOpen(true)}
              className="h-8 gap-2 rounded-lg"
            >
              <Upload className="h-3.5 w-3.5" />
              Upload Capture
            </Button>
            {renderAddSourceMenu('h-8 gap-2 rounded-lg')}
          </div>
        </div>
        <div className="grid gap-2 lg:grid-cols-2">
          {decks.map(renderDeckRow)}
          {videos.map(renderVideoSourceRow)}
        </div>
      </div>
    );
  };

  const isLoading = videosLoading || decksLoading || segmentsLoading || (assetMode === 'slides' && deckSlidesLoading);
  const hasError = videosError || decksError || segmentsError || (assetMode === 'slides' && deckSlidesError);
  const headerSearchValue = assetMode === 'segments' ? segmentSearch : slideSearch;
  const headerSearchPlaceholder = assetMode === 'segments' ? 'Search all segments' : 'Search slides';
  const slideBulkPendingDeletionItems = slideBulkPendingDeletionIds
    .map((slideId) => slideItems.find((item) => item.slideId === slideId))
    .filter(Boolean) as SlideCatalogItem[];

  return (
    <>
      <Helmet>
        <title>Agent Assets | Hobbes</title>
      </Helmet>
      <div className="flex min-h-0 flex-col">
        <PageHeader
          title="Agent Assets"
          titleTooltip="Review what your agent can use now and manage captured agent assets."
        />
        <div className="flex-1 space-y-4 px-4 pb-8 pt-5 sm:px-6 md:px-8">
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              {renderAssetModeTabs()}
              <div className="shrink-0">
                {renderAddSourceMenu('h-10 gap-2 rounded-lg whitespace-nowrap')}
              </div>
            </div>
            {renderTabs()}
            <div className="grid gap-2 sm:grid-cols-[minmax(220px,1fr)_200px_200px] xl:w-[740px]">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={headerSearchValue}
                  onChange={(event) => {
                    if (assetMode === 'segments') {
                      setSegmentSearch(event.target.value);
                    } else {
                      setSlideSearch(event.target.value);
                    }
                  }}
                  placeholder={headerSearchPlaceholder}
                  className="h-9 pl-9"
                />
              </div>
              {renderSourceFilter()}
              {renderGroupFilter(assetMode)}
            </div>
            {assetMode === 'segments' && renderSaveBar()}
            {assetMode === 'slides' && renderSlideGroupSaveBar()}
          </div>

          {videosError && (
            <Alert variant="destructive">
              <AlertDescription>
                Failed to load videos: {videosError instanceof Error ? videosError.message : 'Unknown error'}
              </AlertDescription>
            </Alert>
          )}
          {decksError && (
            <Alert variant="destructive">
              <AlertDescription>
                Failed to load decks: {decksError instanceof Error ? decksError.message : 'Unknown error'}
              </AlertDescription>
            </Alert>
          )}
          {segmentsError && (
            <Alert variant="destructive">
              <AlertDescription>
                Failed to load segments: {segmentsError instanceof Error ? segmentsError.message : 'Unknown error'}
              </AlertDescription>
            </Alert>
          )}
          {deckSlidesError && assetMode === 'slides' && (
            <Alert variant="destructive">
              <AlertDescription>
                Failed to load slides: {deckSlidesError instanceof Error ? deckSlidesError.message : 'Unknown error'}
              </AlertDescription>
            </Alert>
          )}

          {!isLoading && !hasError && renderSourceSetupQueue()}

          {isLoading ? (
            <div className="grid min-h-[620px] items-start gap-4 xl:grid-cols-[minmax(460px,0.96fr)_minmax(460px,1.04fr)]">
              <div className="flex min-h-[620px] min-w-0 flex-col overflow-hidden rounded-[8px] border border-[hsl(var(--input))] bg-background xl:max-h-[calc(100vh-7rem)]">
                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[hsl(var(--input))] px-4 py-3">
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-44" />
                  </div>
                </div>
                <div className="min-h-0 flex-1 space-y-2 overflow-hidden p-3" aria-busy="true" aria-label="Loading agent assets">
                  {Array.from({ length: 6 }).map((_, index) => renderListRowSkeleton(index))}
                </div>
              </div>
              <div className="flex min-h-[460px] flex-col gap-3 rounded-[8px] border border-[hsl(var(--input))] bg-background p-4">
                <Skeleton className="h-5 w-2/3" />
                <Skeleton className="aspect-video w-full rounded-md" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-[80%]" />
                <Skeleton className="h-3 w-[55%]" />
              </div>
            </div>
          ) : hasError ? null : (
            assetMode === 'segments' ? renderWorkspace() : renderSlidesWorkspace()
          )}
        </div>
      </div>

      {availableSegmentGroups.length > 0 && (
        <datalist id="asset-segment-groups">
          {availableSegmentGroups.map((group) => (
            <option key={group} value={group} />
          ))}
        </datalist>
      )}

      {availableSlideGroups.length > 0 && (
        <datalist id="asset-slide-groups">
          {availableSlideGroups.map((group) => (
            <option key={group} value={group} />
          ))}
        </datalist>
      )}

      <VideoUploadDialog
        open={isUploadDialogOpen}
        onOpenChange={setIsUploadDialogOpen}
        onUpload={async (data) => {
          await uploadMutation.mutateAsync(data);
        }}
        isUploading={uploadMutation.isPending}
      />

      <PresentationUploadDialog
        open={isPresentationUploadDialogOpen}
        onOpenChange={setIsPresentationUploadDialogOpen}
        onUploadOne={handleUploadOnePresentation}
        onComplete={handlePresentationUploadComplete}
      />

      <AlertDialog
        open={!!slidePendingDeletion}
        onOpenChange={(open) => {
          if (!open && !deleteSlideMutation.isPending) {
            setSlidePendingDeletion(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this slide?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the slide from Agent Assets and deletes its generated image.
              The source deck stays in place.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {slidePendingDeletion && (
            <div className="rounded-[8px] border border-[hsl(var(--input))] bg-secondary p-3 text-sm">
              <p className="font-medium text-foreground">
                {getSlideTitle(slidePendingDeletion.slide)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {getDeckTitle(slidePendingDeletion.deck)} | Slide {slidePendingDeletion.slide.slide_number}
              </p>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSlideMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteSlideMutation.isPending || !slidePendingDeletion}
              onClick={(event) => {
                event.preventDefault();
                if (slidePendingDeletion) {
                  deleteSlideMutation.mutate(slidePendingDeletion.slideId);
                }
              }}
            >
              {deleteSlideMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                'Delete slide'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={slideBulkPendingDeletionIds.length > 0}
        onOpenChange={(open) => {
          if (!open && !bulkDeleteSlidesMutation.isPending) {
            setSlideBulkPendingDeletionIds([]);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete selected slides?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes {slideBulkPendingDeletionIds.length} discarded slide{slideBulkPendingDeletionIds.length === 1 ? '' : 's'} and generated images.
              Source decks stay in place.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-[8px] border border-[hsl(var(--input))] bg-secondary p-3 text-sm">
            <p className="font-medium text-foreground">
              {slideBulkPendingDeletionIds.length} slide{slideBulkPendingDeletionIds.length === 1 ? '' : 's'} selected
            </p>
            {slideBulkPendingDeletionItems[0] && (
              <p className="mt-1 truncate text-xs text-muted-foreground">
                Including {getSlideTitle(slideBulkPendingDeletionItems[0].slide)}
              </p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleteSlidesMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={bulkDeleteSlidesMutation.isPending || slideBulkPendingDeletionIds.length === 0}
              onClick={(event) => {
                event.preventDefault();
                bulkDeleteSlidesMutation.mutate(slideBulkPendingDeletionIds);
              }}
            >
              {bulkDeleteSlidesMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                'Delete slides'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ContentLibraryPage;
